import MimeUtil from '../../../common/util/MimeUtil';
import MessageBoxUtil from '../../../common/util/MessageBoxUtil';
import Moon from '../../../common/model/global/Moon';
import PreviewEngine from '../../../common/model/preference/model/PreviewEngine';
import Matter from '../../../common/model/matter/Matter';
import BrowserPreviewer from './BrowserPreviewer';
import DownloadToken from '../../../common/model/download/token/DownloadToken';
import User from '../../../common/model/user/User';
import { UserRole } from '../../../common/model/user/UserRole';
import { SpaceMemberRole } from '../../../common/model/space/member/SpaceMemberRole';
import React from 'react';
import ReactDOM from 'react-dom';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';

declare global {
  interface Window {
    MindMapViewer?: any;
    DXFViewer?: any;
    DWGViewer?: any;
  }
}
// 定义一个接口来确保 PreviewEngine 有 name 属性
interface PreviewEngineExtended extends PreviewEngine {
    name: string;
  }
export default class PreviewerHelper {
  private static readonly DIRECT_PREVIEW_TYPES = [
    // 图片格式
    'jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'ico', 'tif', 'tiff', 'ai', 'eps',
    // PDF文档
    'pdf', 'epub', 'mobi',
    // 文本文件
    'txt', 'csv', 'json', 'xml', 'html', 'htm', 'css', 'js', 'md', 'markdown', 'rtf',
    // 配置文件
    'yml', 'yaml', 'ini', 'conf', 'config', 'properties', 'toml',
    // 日志文件
    'log', 'out', 'err',
    // 源代码文件
    'c', 'cpp', 'cc', 'cxx', 'h', 'hpp', 'hh', 'hxx',
    'java', 'class',
    'py', 'pyc', 'pyo', 'pyw',
    'php', 'phtml',
    'rb', 'erb',
    'go',
    'rs',
    'swift',
    'kt', 'kts',
    'scala',
    'pl', 'pm',
    'r', 'R',
    'm', 'mm',
    'cs',
    'vb',
    'fs', 'fsx', 'fsi',
    'clj', 'cljs', 'cljc',
    'lua',
    'sh', 'bash', 'zsh', 'fish', 'ps1', 'bat', 'cmd',
    'sql',
    'dockerfile', 'dockerignore',
    'gitignore', 'gitattributes', 'gitmodules',
    // 数据文件
    'tsv', 'xml', 'jsonl', 'ndjson',
    // 网页相关
    'vue', 'jsx', 'tsx', 'ts', 'sass', 'scss', 'less', 'styl',
    // 音视频格式
    'mp3', 'wav', 'ogg', 'm4a', 'aac', 'flac', 'wma', 'aiff',
    'mp4', 'webm', 'ogg', 'mov', 'avi', 'mkv', 'flv', 'wmv', 'm4v', '3gp', 'mpeg', 'mpg'
  ];

  // MODIFIED: 扩展 Office 预览类型，加入国产 WPS、OpenOffice、Visio、Photoshop、3D模型、压缩包、SVG 等
  private static readonly OFFICE_PREVIEW_TYPES = [
    'doc', 'docx', 'ppt', 'pptx', 'xls', 'xlsx',
      // 新增 Word 模板
    'dot', 'dotx', 'dotm',
    // 国产 WPS Office
    'wps', 'dps', 'et', 'ett', 'wpt',
    // OpenOffice / LibreOffice
    'odt', 'ods', 'ots', 'odp', 'otp', 'six', 'ott', 'fodt', 'fods',
    // Visio
    'vsd', 'vsdx',
    // Windows 图像
    'wmf', 'emf',
      // 医疗数位影像
    'dcm', 'dicom', 'dcmdir',
    // Photoshop
    'psd',
    // 工作流
    'bpmn',
    // 邮件
    'eml',
    // 图书
    'epub',
    // 3D 模型
    'obj', '3ds', 'stl', 'ply', 'gltf', 'glb', 'off', '3dm', 'fbx', 'dae', 'wrl', '3mf', 'ifc', 'brep', 'step', 'iges', 'fcstd', 'bim',
    // 压缩包
    'zip', 'rar', 'jar', 'tar', 'gzip', '7z',
    // SVG 矢量图像
    'svg'
  ];

  private static readonly DXF_PREVIEW_TYPES = ['dxf'];

  private static readonly ODA_CAD_TYPES = ['dwg'];

  private static readonly MINDMAP_PREVIEW_TYPES = [
    'xmind', 'xmt', 'xmap', 'mmap', 'mmat', 'mm', 'mindnode',
    'mindmup', 'mup', 'nm5', 'nm4', 'nm3', 'imx', 'imm',
    'mem', 'mmp', 'cdmz', 'cdmm', 'coggle', 'mind', 'km',
    'mindmap', 'mn', 'mnd', 'pos', 'eddx', 'mindjet', 'mj',
    'mjt', 'mjp', 'brain', 'brn', 'thought', 'tht'
  ];

  private static readonly CAD_PREVIEW_TYPES = [
    ...this.DXF_PREVIEW_TYPES,
    ...this.ODA_CAD_TYPES
  ];

  private static watermarkConfig: any = null;

  // 新增：显示预览失败消息（不提供下载）
  private static showPreviewFailedMessage(fileName: string, error?: any): void {
    const errorMessage = error instanceof Error ? error.message : (typeof error === 'string' ? error : '未知错误');
    MessageBoxUtil.error(`文件 "${fileName}" 预览失败: ${errorMessage}`);
    
    console.warn('预览失败详情:', {
        fileName,
        error: error?.message || error,
        stack: error?.stack
    });
  }

  // 私有静态方法：字符串转哈希值
  private static stringToHash(str: string): number {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return Math.abs(hash);
  }

  public static async previewRemovableFile(
    fileInfo: RemovableFileInfo,
    deviceId: string,
    filePath: string,
    downloadUrl: string,
    hasDownloadPermission: boolean = true,
    hasPrintPermission: boolean = false
  ): Promise<void> {
    console.log('=== 移动盘文件预览 - 完整流程 ===');
    console.log('文件名:', fileInfo.name);
    console.log('设备ID:', deviceId);
    console.log('文件路径:', filePath);
    console.log('=======================');
    
    const fileName = fileInfo.name;
    const extension = MimeUtil.getExtensionWithoutDot(fileName)?.toLowerCase();
    
    // 创建一个虚拟的 Matter 对象
    const matter = new Matter();
    matter.uuid = `removable-${deviceId}-${encodeURIComponent(filePath)}`;
    matter.name = fileName;
    matter.size = fileInfo.size || 0;
    matter.privacy = false;
    matter.spaceUuid = 'removable';
    matter.userUuid = 'removable';
    
    // 标记为移动盘文件
    (matter as any).isRemovable = true;
    (matter as any).removableDeviceId = deviceId;
    (matter as any).removableFilePath = filePath;
    (matter as any).removableDownloadUrl = downloadUrl;
    
    console.log('文件扩展名:', extension);
    console.log('文件大小:', fileInfo.size);
    
    // 构建完整的预览URL - 使用预览接口
    const previewUrl = this.buildRemovablePreviewUrl(deviceId, filePath, matter.name);
    
    console.log('预览URL:', previewUrl);
    
    try {
      // 使用增强的移动盘预览方法
      await this.enhancedPreviewRemovableFile(
        matter,
        previewUrl,
        hasDownloadPermission,
        hasPrintPermission
      );
    } catch (error) {
      console.error('文件预览失败:', error);
      // 不再调用openDownloadDialog，只显示预览失败信息
      this.showPreviewFailedMessage(fileName, error);
    }
  }

  private static async handleExternalPreviewForRemovable(
    matter: Matter,
    previewEngine: PreviewEngine,
    absoluteFileUrl: string,
    hasDownloadPermission?: boolean,
    hasPrintPermission?: boolean
  ): Promise<void> {
    try {
      console.log('🌐 移动盘外部预览引擎处理');
      
      // 👇 直接使用 replaceUrlPlaceholders，让 previewEngine.url 决定一切 👇
      // 如果 previewEngine.url 是 "http://kk:8012/onlinePreview?url={url}"，
      // 那么结果自然就是正确的绝对URL。
      let finalUrl = this.replaceUrlPlaceholders(previewEngine.url, absoluteFileUrl, matter.name);
  
      // ✅ 修复：生成水印参数
      const watermark = await this.generateWatermarkParams(matter);
      console.log('移动盘外部预览水印状态:', watermark ? '已启用' : '未启用');
    
      if (previewEngine.previewInSite) {
        BrowserPreviewer.show(
          matter.name,
          finalUrl,
          matter.size,
          watermark, // ✅ 修复：传递水印参数
          hasDownloadPermission,
          hasPrintPermission,
          matter
        );
      } else {
        window.open(finalUrl);
      }
    } catch (error) {
      console.error('移动盘外部预览失败:', error);
      this.showPreviewFailedMessage(matter.name, error);
    }
  }

// 新增：为移动盘文件构建kkFileView URL（避免影响本地文件）
private static buildKkFileViewUrlForRemovable(
  fileUrl: string,
  fileName: string,
  hasDownloadPermission?: boolean,
  hasPrintPermission?: boolean
): string {
  // 直接 Base64 编码整个 URL（kkFileView 要求）
  const base64Url = btoa(encodeURIComponent(fileUrl));

  const params = new URLSearchParams({
    url: base64Url,
    fullfilename: fileName,
    preview: 'true',
    type: 'office',
    officePreviewType: 'pdf',
    watermark: 'false',
    'office.viewState': 'show',
    _t: Date.now().toString()
  });

  if (hasDownloadPermission !== undefined) {
    params.set('download', hasDownloadPermission ? '1' : '0');
  }
  if (hasPrintPermission !== undefined) {
    params.set('print', hasPrintPermission ? '1' : '0');
  }

  // 设置具体类型
  const ext = fileName.split('.').pop()?.toLowerCase();
  if (ext === 'ppt' || ext === 'pptx') {
    params.set('type', 'ppt');
  } else if (ext === 'doc' || ext === 'docx') {
    params.set('type', 'doc');
  } else if (ext === 'xls' || ext === 'xlsx') {
    params.set('type', 'excel');
  }

  return `/onlinePreview?${params.toString()}`; // ✅ 已修复：移除空格
}

// 新增：构建简单的移动盘预览URL
private static buildSimpleRemovablePreviewUrl(
  deviceId: string,
  filePath: string,
  fileName: string
): string {
  try {
    console.log('🔧 构建简单预览URL');
    
    // 清理路径
    const cleanPath = filePath
      .replace(/\\/g, '/')
      .replace(/\/+/g, '/')
      .replace(/^\//, '')
      .trim();
    
    // 只保留必要参数
    const params = new URLSearchParams({
      deviceId: deviceId,
      path: cleanPath,
      preview: 'true',
      disposition: 'inline',
      _t: Date.now().toString()
    });
    
    // 注意：不要对参数进行双重编码
    // URLSearchParams会自动编码，所以这里直接传递原始值
    params.set('filename', fileName);
    
    return `/api/removable/preview?${params.toString()}`;
    
  } catch (error) {
    console.error('构建简单预览URL失败:', error);
    throw error;
  }
}

// 新增：正确的Base64编码方法（兼容中文）
private static base64Encode(str: string): string {
  try {
    // 先进行URL编码，确保中文字符被正确处理
    const encoded = encodeURIComponent(str);
    // 然后进行Base64编码
    return btoa(encoded);
  } catch (error) {
    console.error('Base64编码失败:', error);
    // 备用方案
    return btoa(str);
  }
}

// 新增：按照kkFileView规范构建移动盘Office预览URL
private static async buildKkFileViewUrlForRemovableOffice(
  matter: Matter,
  hasDownloadPermission?: boolean,
  hasPrintPermission?: boolean
): Promise<string> {
  try {
    const deviceId = (matter as any).removableDeviceId;
    const filePath = (matter as any).removableFilePath;
    const fileName = matter.name;
    
    console.log('🔧 按照kkFileView规范构建URL:', {
      deviceId: deviceId.substring(0, 10) + '...',
      filePath: filePath.substring(0, 50) + '...',
      fileName
    });
    
    // 1. 构建简单的移动盘预览URL（不要过多参数）
    const previewUrl = this.buildSimpleRemovablePreviewUrl(deviceId, filePath, fileName);
    console.log('🔗 简单预览URL:', previewUrl);
    
    // 2. 构建绝对URL
    const absoluteUrl = this.buildAbsoluteUrl(previewUrl);
    console.log('🔗 绝对URL（前100字符）:', absoluteUrl.substring(0, 100) + '...');
    
    // 3. 关键：按照kkFileView文档进行编码
    // 格式：base64Encode(url) -> encodeURIComponent(base64)
    const base64Encoded = this.base64Encode(absoluteUrl);
    console.log('🔐 Base64编码（前50字符）:', base64Encoded.substring(0, 50) + '...');
    
    const urlEncoded = encodeURIComponent(base64Encoded);
    console.log('🔐 URL编码后的Base64（前50字符）:', urlEncoded.substring(0, 50) + '...');
    
    // 4. 构建kkFileView参数
    const params = new URLSearchParams({
      url: urlEncoded,  // 关键：传递编码后的Base64字符串
      fullfilename: fileName,
      preview: 'true'
    });
    
    // 5. 添加Office相关参数
    const extension = fileName.split('.').pop()?.toLowerCase() || '';
    if (extension === 'ppt' || extension === 'pptx') {
      params.set('type', 'ppt');
      params.set('officePreviewType', 'pdf');
    } else if (extension === 'doc' || extension === 'docx') {
      params.set('type', 'doc');
      params.set('officePreviewType', 'pdf');
    } else if (extension === 'xls' || extension === 'xlsx') {
      params.set('type', 'excel');
      params.set('officePreviewType', 'pdf');
    }
    
    // 6. 添加权限参数
    if (hasDownloadPermission !== undefined) {
      params.set('download', hasDownloadPermission ? '1' : '0');
    }
    if (hasPrintPermission !== undefined) {
      params.set('print', hasPrintPermission ? '1' : '0');
    }
    
    const kkUrl = `/onlinePreview?${params.toString()}`; // ✅ 已修复：移除空格
    console.log('✅ 最终的kkFileView URL（前200字符）:', kkUrl.substring(0, 200) + '...');
    
    return kkUrl;
    
  } catch (error) {
    console.error('构建kkFileView URL失败:', error);
    throw error;
  }
}
// 完全重写移动盘预览URL构建逻辑
private static async enhancedPreviewRemovableFile(
  matter: Matter,
  previewUrl: string,
  hasDownloadPermission?: boolean,
  hasPrintPermission?: boolean
): Promise<void> {
  const fileName = matter.name;
  const extension = MimeUtil.getExtensionWithoutDot(fileName)?.toLowerCase();
  
  console.log('=== 移动盘文件预览 ===');
  console.log('文件名:', fileName);
  console.log('扩展名:', extension);
  
  const downloadPermission = hasDownloadPermission ?? true;
  const printPermission = hasPrintPermission ?? false;
  
  // 1. 思维导图文件
  if (this.isMindMapType(extension)) {
    console.log('🧠 思维导图文件');
    const watermark = await this.generateWatermarkParams(matter);
    await this.handleMindMapPreview(matter, previewUrl, watermark, downloadPermission, printPermission);
    return;
  }
  
  // 2. CAD文件
  if (this.isCadType(extension)) {
    console.log('🎯 CAD文件');
    if (this.isDXFType(extension)) {
      console.log('📐 DXF文件');
      const watermark = await this.generateWatermarkParams(matter);
      await this.handleDXFPreview(matter, previewUrl, watermark, downloadPermission, printPermission);
    } else if (this.isDWGType(extension)) {
      console.log('🔄 DWG文件');
      const cadPreviewUrl = this.addCadPreviewParams(previewUrl, extension);
      const watermark = await this.generateWatermarkParams(matter);
      await this.handleDWGConversionPreview(matter, cadPreviewUrl, watermark, downloadPermission, printPermission);
    }
    return;
  }
  
  // 3. 直接预览类型（图片、PDF、文本、音视频）
  if (this.isDirectPreviewType(extension)) {
    console.log('📄 直接预览类型');
    
    if (extension === 'pdf') {
      console.log('📚 PDF文件');
      const enhancedPdfUrl = this.enhancePdfPreviewUrl(previewUrl, matter.name);
      const watermark = await this.generateWatermarkParams(matter);
      
      BrowserPreviewer.show(
        matter.name,
        enhancedPdfUrl,
        matter.size,
        watermark,
        downloadPermission,
        printPermission,
        matter
      );
      return;
    }
    
    if (this.isTextType(extension)) {
      console.log('📝 文本文件');
      const enhancedTextUrl = this.enhanceTextPreviewUrl(previewUrl, matter.name);
      const watermark = await this.generateWatermarkParams(matter);
      
      BrowserPreviewer.show(
        matter.name,
        enhancedTextUrl,
        matter.size,
        watermark,
        downloadPermission,
        printPermission,
        matter
      );
      return;
    }
    
    // 其他直接预览类型
    const watermark = await this.generateWatermarkParams(matter);
    BrowserPreviewer.show(
      matter.name,
      previewUrl,
      matter.size,
      watermark,
      downloadPermission,
      printPermission,
      matter
    );
    return;
  }
  

  if (this.isOfficeType(extension)) {
    console.log('📊 Office文档 - 使用 Token 预览（修复版）');
    try {
        // 获取设备ID和文件路径
        const deviceId = (matter as any).removableDeviceId;
        const filePath = (matter as any).removableFilePath;
        const fileName = matter.name;

        // 获取预览令牌
        const tokenResponse = await this.fetchRemovablePreviewToken(deviceId, filePath, fileName);
        if (!tokenResponse || !tokenResponse.token) {
            throw new Error('获取预览令牌失败');
        }
        console.log('✅ 获取到预览令牌');

        // ✅ 修复点：使用 URLSearchParams 正确构建 URL
        const previewParams = new URLSearchParams({
            token: tokenResponse.token,
            filename: fileName,
            disposition: 'inline',
            preview: 'true'
        });
        const tokenPreviewUrl = `/api/removable/preview?${previewParams.toString()}`;

        const absoluteFileUrl = this.buildAbsoluteUrl(tokenPreviewUrl);
        console.log('🔗 带Token的绝对文件URL:', absoluteFileUrl);

        // 查找预览引擎
        const previewEngine = this.findSuitablePreviewEngine(fileName);
        if (!previewEngine) {
            throw new Error('未找到Office预览引擎');
        }

        // 使用通用方法处理
        await this.handleExternalPreviewForRemovable(
            matter,
            previewEngine,
            absoluteFileUrl,
            hasDownloadPermission,
            hasPrintPermission
        );
    } catch (error) {
        console.error('Office预览失败:', error);
        this.showPreviewFailedMessage(fileName, error);
    }
    return;
}
  // 5. 其他文件类型
  console.log('📦 其他文件类型');
  this.showPreviewFailedMessage(fileName, '不支持的文件格式');
}

// 新增：为Office文件构建简单预览URL
private static buildSimpleRemovablePreviewUrlForOffice(
  deviceId: string,
  filePath: string,
  fileName: string
): string {
  try {
    console.log('🔧 为Office文件构建简单预览URL');
    
    // 清理路径
    const cleanPath = filePath
      .replace(/\\/g, '/')
      .replace(/\/+/g, '/')
      .replace(/^\//, '')
      .trim();
    
    // 使用最简单的参数
    const params = new URLSearchParams({
      deviceId: deviceId,
      path: cleanPath,
      preview: 'true',
      disposition: 'inline',
      _t: Date.now().toString()
    });
    
    // 注意：filename参数不加，避免编码问题
    // params.set('filename', fileName);
    
    return `/api/removable/preview?${params.toString()}`;
    
  } catch (error) {
    console.error('构建简单预览URL失败:', error);
    throw error;
  }
}

  // 新增方法：增强PDF预览URL
  private static enhancePdfPreviewUrl(previewUrl: string, fileName: string): string {
    try {
      // 确保是预览接口
      if (previewUrl.includes('/api/removable/download')) {
        previewUrl = previewUrl.replace('/api/removable/download', '/api/removable/preview');
      }
      
      const url = new URL(previewUrl, window.location.origin);
      const params = url.searchParams;
      
      // 强制设置PDF预览参数
      params.set('pdfPreview', 'true');
      params.set('toolbar', '1');
      params.set('disposition', 'inline');
      params.set('preview', 'true');
      params.set('fullfilename', fileName);
      params.set('_t', Date.now().toString());
      
      // 确保响应头正确
      params.set('responseContentDisposition', 'inline');
      params.set('responseContentType', 'application/pdf');
      
      return url.toString();
    } catch (e) {
      console.warn('PDF URL增强失败:', e);
      
      // 回退方案：手动构建URL，确保使用预览接口
      if (previewUrl.includes('/api/removable/download')) {
        previewUrl = previewUrl.replace('/api/removable/download', '/api/removable/preview');
      }
      
      if (previewUrl.includes('?')) {
        return `${previewUrl}&pdfPreview=true&toolbar=1&disposition=inline&_t=${Date.now()}`;
      } else {
        return `${previewUrl}?pdfPreview=true&toolbar=1&disposition=inline&_t=${Date.now()}`;
      }
    }
  }

  // 新增方法：添加CAD预览参数
  private static addCadPreviewParams(previewUrl: string, extension?: string): string {
    try {
      // 确保是预览接口
      if (previewUrl.includes('/api/removable/download')) {
        previewUrl = previewUrl.replace('/api/removable/download', '/api/removable/preview');
      }
      
      const url = new URL(previewUrl, window.location.origin);
      const params = url.searchParams;
      
      // 添加CAD转换参数
      if (this.isDWGType(extension)) {
        params.set('cadConversion', 'true');
        params.set('outputFormat', 'dxf');
        params.set('quality', 'high');
      }
      
      // 添加预览参数
      params.set('preview', 'true');
      params.set('disposition', 'inline');
      params.set('_t', Date.now().toString());
      
      return url.toString();
    } catch (e) {
      console.warn('CAD URL增强失败:', e);
      // 确保使用预览接口
      if (previewUrl.includes('/api/removable/download')) {
        previewUrl = previewUrl.replace('/api/removable/download', '/api/removable/preview');
      }
      return previewUrl;
    }
  }

  // 修改：构建带令牌的预览URL，兼容本地和移动盘
  private static buildPreviewUrlWithToken(
    token: string, 
    fileName: string,
    matter?: Matter
  ): string {
    try {
      // 如果是移动盘文件
      if (matter && (matter as any).isRemovable) {
        const deviceId = (matter as any).removableDeviceId;
        const filePath = (matter as any).removableFilePath;
        
        const params = new URLSearchParams({
          token: token,
          deviceId: deviceId,
          path: filePath,
          filename: fileName,
          disposition: 'inline',
          preview: 'true',
          _t: Date.now().toString()
        });
        
        return `/api/removable/preview?${params.toString()}`;
      } else {
        // 本地文件（使用原始逻辑）
        const params = new URLSearchParams({
          token: token,
          filename: fileName,
          disposition: 'inline',
          preview: 'true',
          _t: Date.now().toString()
        });
        
        return `/api/removable/preview?${params.toString()}`;
      }
    } catch (error) {
      console.error('构建令牌URL失败:', error);
      
      // 备选方案
      if (matter && (matter as any).isRemovable) {
        const deviceId = (matter as any).removableDeviceId;
        const filePath = (matter as any).removableFilePath;
        return `/api/removable/preview?token=${token}&deviceId=${deviceId}&path=${encodeURIComponent(filePath)}&filename=${encodeURIComponent(fileName)}`;
      } else {
        return `/api/removable/preview?token=${token}&filename=${encodeURIComponent(fileName)}`;
      }
    }
  }

  // 新增：构建预览服务URL（用于kkFileView等第三方服务）
  private static buildPreviewServiceUrl(
    fileUrl: string,
    fileName: string,
    hasDownloadPermission?: boolean,
    hasPrintPermission?: boolean
  ): string {
    try {
      // 1. 构建完整URL（确保是绝对URL）
      let fullFileUrl: string;
      
      try {
        fullFileUrl = new URL(fileUrl, window.location.origin).href;
      } catch (e) {
        // 如果URL已经是完整的，直接使用
        if (fileUrl.startsWith('http://') || fileUrl.startsWith('https://')) {
          fullFileUrl = fileUrl;
        } else {
          // 相对路径，添加当前域名
          fullFileUrl = `${window.location.origin}${fileUrl.startsWith('/') ? '' : '/'}${fileUrl}`;
        }
      }
      
      console.log('🔗 完整文件URL:', fullFileUrl);
      
      // 2. 安全编码（Base64）
      const safeBase64 = (str: string): string => {
        try {
          // 先进行URL编码，再Base64
          const encoded = encodeURIComponent(str);
          return btoa(encoded);
        } catch (e) {
          console.warn('Base64编码失败，使用备用方案:', e);
          // 备用方案：直接对原始URL进行Base64
          return btoa(str);
        }
      };
      
      const encodedFileUrl = safeBase64(fullFileUrl);
      console.log('🔐 Base64编码后的URL:', encodedFileUrl.substring(0, 50) + '...');
      
      // 3. 构建预览服务参数
      const params = new URLSearchParams({
        url: encodedFileUrl,
        fullfilename: fileName,
        preview: 'true',
        disposition: 'inline',
        type: 'office',
        officePreviewType: 'pdf',
        watermark: 'false', // 水印在前端处理
        'office.viewState': 'show',
        download: hasDownloadPermission ? '1' : '0',
        print: hasPrintPermission ? '1' : '0',
        _t: Date.now().toString()
      });
      
      // 4. 如果是无权限，禁用右键
      const hasAnyPermission = hasDownloadPermission || hasPrintPermission;
      if (!hasAnyPermission) {
        params.set('disableRightClick', 'true');
      }
      
      // 5. 添加缓存控制
      params.set('cache', 'false');
      params.set('nocache', Date.now().toString());
      
      return `/onlinePreview?${params.toString()}`; // ✅ 已修复：移除空格
    } catch (error) {
      console.error('构建预览服务URL失败:', error);
      
      // 备选方案：简单的参数
      return `/onlinePreview?url=${encodeURIComponent(fileUrl)}&fullfilename=${encodeURIComponent(fileName)}`; // ✅ 已修复：移除空格
    }
  }
  // 新增：获取移动盘预览令牌
  private static async fetchRemovablePreviewToken(
    deviceId: string,
    filePath: string,
    fileName: string
  ): Promise<{ token: string; success: boolean }> {
    try {
      const params = new URLSearchParams({
        deviceId: deviceId,
        path: filePath,
        filename: fileName,
        preview: 'true',
        disposition: 'inline',
        _t: Date.now().toString()
      });

      const url = `/api/removable/preview/token?${params.toString()}`;
      console.log('🔐 请求预览令牌URL:', url);

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        credentials: 'include'
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('令牌请求失败:', {
          status: response.status,
          statusText: response.statusText,
          error: errorText
        });
        throw new Error(`令牌请求失败: ${response.status}`);
      }

      const result = await response.json();
      console.log('🔐 令牌响应数据:', result);

      // 根据后台返回格式适配
      if (result.success === false) {
        throw new Error(result.message || '获取预览令牌失败');
      }

      // 支持多种返回格式
      const token = result.token || result.data?.token || result.data;
      if (!token) {
        console.error('令牌不存在于响应中:', result);
        throw new Error('无效的令牌响应');
      }

      return {
        token: token,
        success: true
      };
    } catch (error) {
      console.error('获取移动盘预览令牌失败:', error);
      throw error;
    }
  }

// 修改：移动盘Office预览备选方案 - 统一使用JWT令牌
private static async fallbackOfficePreviewForRemovable(
  matter: Matter,
  previewUrl: string,
  hasDownloadPermission?: boolean,
  hasPrintPermission?: boolean
): Promise<void> {
  console.log('🔄 移动盘Office文件预览方案 - 使用JWT令牌流程');

  try {
    // 1. 获取设备ID和文件路径
    const deviceId = (matter as any).removableDeviceId;
    const filePath = (matter as any).removableFilePath;
    const fileName = matter.name;

    if (!deviceId || !filePath) {
      console.error('❌ 移动盘文件缺少必要信息');
      this.showPreviewFailedMessage(matter.name, '移动盘文件缺少必要信息');
      return;
    }

    console.log('📋 移动盘文件信息:', {
      deviceId,
      filePath,
      fileName
    });

    // 2. 获取JWT预览令牌（调用后台的JWT令牌接口）
    const tokenResponse = await this.fetchRemovableJwtPreviewToken(deviceId, filePath, fileName);
    
    if (!tokenResponse || !tokenResponse.token) {
      console.error('❌ 获取JWT预览令牌失败');
      this.showPreviewFailedMessage(matter.name, '获取预览令牌失败');
      return;
    }

    console.log('✅ 获取到JWT预览令牌:', tokenResponse.token.substring(0, 20) + '...');

    // 3. 构建最终预览URL（使用标准预览接口，传递JWT令牌）
    const finalPreviewUrl = this.buildRemovablePreviewUrlWithJwt(
      tokenResponse.token,
      fileName,
      hasDownloadPermission,
      hasPrintPermission
    );

    console.log('🌐 最终预览URL:', finalPreviewUrl);

    // 4. 使用BrowserPreviewer显示
    const watermark = await this.generateWatermarkParams(matter);
    
    BrowserPreviewer.show(
      matter.name,
      finalPreviewUrl,
      matter.size,
      watermark,
      hasDownloadPermission,
      hasPrintPermission,
      matter
    );

  } catch (error) {
    console.error('移动盘Office预览失败:', error);
    this.showPreviewFailedMessage(matter.name, error);
  }
}
// 新增：获取JWT预览令牌
private static async fetchRemovableJwtPreviewToken(
  deviceId: string,
  filePath: string,
  fileName: string
): Promise<{ token: string; expiresAt: number }> {
  try {
    const params = new URLSearchParams({
      deviceId: deviceId,
      path: filePath,
      filename: fileName,
      preview: 'true',
      disposition: 'inline',
      _t: Date.now().toString()
    });

    const url = `/api/removable/preview/token?${params.toString()}`;
    console.log('🔐 请求JWT令牌URL:', url);

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      credentials: 'include'
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('JWT令牌请求失败:', {
        status: response.status,
        statusText: response.statusText,
        error: errorText
      });
      throw new Error(`JWT令牌请求失败: ${response.status}`);
    }

    const result = await response.json();
    console.log('🔐 JWT令牌响应数据:', result);

    if (!result.success) {
      throw new Error(result.msg || '获取JWT令牌失败');
    }

    const token = result.data?.token || result.token;
    const expiresAt = result.data?.expiresAt || result.expiresAt;

    if (!token) {
      console.error('JWT令牌不存在于响应中:', result);
      throw new Error('无效的JWT令牌响应');
    }

    return {
      token: token,
      expiresAt: expiresAt || Date.now() + 300000 // 默认5分钟
    };
  } catch (error) {
    console.error('获取JWT预览令牌失败:', error);
    throw error;
  }
}

// 新增：构建带JWT令牌的预览URL
private static buildRemovablePreviewUrlWithJwt(
  jwtToken: string,
  fileName: string,
  hasDownloadPermission?: boolean,
  hasPrintPermission?: boolean
): string {
  try {
    // 使用标准预览接口，传递JWT令牌
    const params = new URLSearchParams({
      token: jwtToken,
      filename: fileName,
      preview: 'true',
      disposition: 'inline',
      _t: Date.now().toString()
    });

    // 添加权限参数
    if (hasDownloadPermission !== undefined) {
      params.set('download', hasDownloadPermission ? '1' : '0');
    }
    if (hasPrintPermission !== undefined) {
      params.set('print', hasPrintPermission ? '1' : '0');
    }

    // 标记为Office预览
    params.set('officePreview', 'true');
    params.set('type', 'office');

    return `/api/removable/preview?${params.toString()}`; // ✅ 已修复：移除空格
  } catch (error) {
    console.error('构建JWT预览URL失败:', error);
    return `/api/removable/preview?token=${jwtToken}&filename=${encodeURIComponent(fileName)}`; // ✅ 已修复：移除空格
  }
}
// 新增：构建带令牌的移动盘预览URL（简化版）
private static buildRemovablePreviewUrlWithToken(
  deviceId: string,
  filePath: string,
  fileName: string,
  token: string
): string {
  try {
    // 构建简单的预览URL，包含必要参数
    const params = new URLSearchParams({
      token: token,
      deviceId: deviceId,
      path: filePath,
      filename: fileName,
      preview: 'true',
      disposition: 'inline',
      _t: Date.now().toString()
    });
    
    return `/api/removable/preview?${params.toString()}`;
  } catch (error) {
    console.error('构建令牌预览URL失败:', error);
    // 备选方案
    return `/api/removable/preview?token=${token}&deviceId=${deviceId}&path=${encodeURIComponent(filePath)}&filename=${encodeURIComponent(fileName)}`;
  }
}
// 新增：构建第三方预览服务URL（针对移动盘文件优化）
private static buildThirdPartyPreviewUrl(
  fileUrl: string,
  fileName: string,
  previewEngine: PreviewEngine,
  hasDownloadPermission?: boolean,
  hasPrintPermission?: boolean
): string {
  try {

    
    // 如果是kkFileView或类似服务
    if (previewEngine.url.includes('onlinePreview') || previewEngine.url.includes('{url}')) {
      return this.buildKkFileViewUrl(fileUrl, fileName, hasDownloadPermission, hasPrintPermission);
    }
    
    // 其他预览服务
    return this.replaceUrlPlaceholders(previewEngine.url, fileUrl, fileName);
    
  } catch (error) {
    console.error('构建第三方预览URL失败:', error);
    // 默认使用kkFileView
    return this.buildKkFileViewUrl(fileUrl, fileName, hasDownloadPermission, hasPrintPermission);
  }
}

private static buildKkFileViewUrl(
  fileUrl: string,
  fileName: string,
  hasDownloadPermission?: boolean,
  hasPrintPermission?: boolean
): string {
  try {
    // 确保fileUrl是完整的URL（如果是相对路径，添加当前域名）
    let fullFileUrl = fileUrl;
    if (!fileUrl.startsWith('http://') && !fileUrl.startsWith('https://')) {
      // 相对路径，添加当前域名
      const baseUrl = window.location.origin;
      fullFileUrl = `${baseUrl}${fileUrl.startsWith('/') ? '' : '/'}${fileUrl}`;
    }
    
    console.log('📡 完整文件URL:', fullFileUrl);
    
    // Base64编码（不使用双重URL编码，直接Base64）
    const encodedFileUrl = btoa(encodeURIComponent(fullFileUrl));
    console.log('🔐 Base64编码后的URL:', encodedFileUrl.substring(0, 50) + '...');
    
    // 构建预览参数
    const params = new URLSearchParams({
      url: encodedFileUrl,
      fullfilename: fileName,
      preview: 'true',
      disposition: 'inline',
      type: 'office',
      officePreviewType: 'pdf',
      watermark: 'false',
      'office.viewState': 'show',
      download: hasDownloadPermission ? '1' : '0',
      print: hasPrintPermission ? '1' : '0',
      _t: Date.now().toString()
    });
    
    // 针对移动盘文件的特殊处理
    params.set('source', 'removable');
    params.set('cache', 'false');
    
    return `/onlinePreview?${params.toString()}`; // ✅ 已修复：移除空格
  } catch (error) {
    console.error('构建kkFileView URL失败:', error);
    
    // 简单的备选方案
    return `/onlinePreview?url=${encodeURIComponent(fileUrl)}&fullfilename=${encodeURIComponent(fileName)}`; // ✅ 已修复：移除空格
  }
}

// 新增：调试方法，用于测试预览服务
private static async testPreviewServiceConnection(fileUrl: string): Promise<boolean> {
  try {
    console.log('🔍 测试预览服务连接...');
    
    // 1. 先测试文件URL是否可访问
    const fileResponse = await fetch(fileUrl, { method: 'HEAD' });
    if (!fileResponse.ok) {
      console.error('❌ 文件URL不可访问:', fileResponse.status, fileResponse.statusText);
      return false;
    }
    
    console.log('✅ 文件URL可访问');
    
    // 2. 测试预览服务是否可用
    const testParams = new URLSearchParams({
      url: btoa(encodeURIComponent(fileUrl)),
      fullfilename: 'test.pptx',
      preview: 'true',
      disposition: 'inline',
      _t: Date.now().toString()
    });
    
    const previewTestUrl = `/onlinePreview?${testParams.toString()}`;
    const previewResponse = await fetch(previewTestUrl, { method: 'HEAD' });
    
    if (previewResponse.ok) {
      console.log('✅ 预览服务可用');
      return true;
    } else {
      console.error('❌ 预览服务不可用:', previewResponse.status, previewResponse.statusText);
      return false;
    }
    
  } catch (error) {
    console.error('测试预览服务失败:', error);
    return false;
  }
}
  // 新增方法：增强文本预览URL
  private static enhanceTextPreviewUrl(previewUrl: string, fileName: string): string {
    try {
      // 确保是预览接口
      if (previewUrl.includes('/api/removable/download')) {
        previewUrl = previewUrl.replace('/api/removable/download', '/api/removable/preview');
      }
      
      const url = new URL(previewUrl, window.location.origin);
      const params = url.searchParams;
      
      // 强制设置文本预览参数
      params.set('textPreview', 'true');
      params.set('encoding', 'utf-8');
      params.set('disposition', 'inline');
      params.set('preview', 'true');
      params.set('fullfilename', fileName);
      params.set('_t', Date.now().toString());
      
      // 确保响应头正确
      params.set('responseContentDisposition', 'inline');
      params.set('responseContentType', 'text/plain;charset=utf-8');
      
      return url.toString();
    } catch (e) {
      console.warn('文本URL增强失败:', e);
      
      // 回退方案，确保使用预览接口
      if (previewUrl.includes('/api/removable/download')) {
        previewUrl = previewUrl.replace('/api/removable/download', '/api/removable/preview');
      }
      
      if (previewUrl.includes('?')) {
        return `${previewUrl}&textPreview=true&encoding=utf-8&disposition=inline&_t=${Date.now()}`;
      } else {
        return `${previewUrl}?textPreview=true&encoding=utf-8&disposition=inline&_t=${Date.now()}`;
      }
    }
  }
 
// 修改：构建绝对URL（简化）
private static buildAbsoluteUrl(relativeUrl: string): string {
  try {
    // 如果已经是绝对URL，直接返回
    if (relativeUrl.startsWith('http://') || relativeUrl.startsWith('https://')) {
      return relativeUrl;
    }
    
    // 构建绝对URL
    const baseUrl = window.location.origin;
    const absoluteUrl = `${baseUrl}${relativeUrl.startsWith('/') ? '' : '/'}${relativeUrl}`;
    
    return absoluteUrl;
    
  } catch (error) {
    console.error('构建绝对URL失败:', error);
    return relativeUrl;
  }
}
// 修改：构建移动盘预览URL（简化版）
private static buildRemovablePreviewUrl(deviceId: string, filePath: string, fileName: string): string {
  try {
    console.log('🔧 构建移动盘预览URL（简化版）');
    
    // 清理路径
    const cleanPath = filePath
      .replace(/\\/g, '/')
      .replace(/\/+/g, '/')
      .replace(/^\//, '')
      .trim();
    
    // 构建参数
    const params = new URLSearchParams({
      deviceId: deviceId,
      path: cleanPath,
      preview: 'true',
      disposition: 'inline',
      _t: Date.now().toString()
    });
    
    // 注意：不要对参数值进行双重编码
    // URLSearchParams会自动编码，所以这里传递原始值
    params.set('filename', fileName);
    
    return `/api/removable/preview?${params.toString()}`;
    
  } catch (error) {
    console.error('构建预览URL失败:', error);
    return `/api/removable/preview?deviceId=${deviceId}&path=${encodeURIComponent(filePath)}`;
  }
}

/**
 * 专用于移动盘 Office 文件的预览（不依赖 Matter UUID）
 * @param fileName 原始文件名（必须包含扩展名，如 xxx.pptx）
 * @param fileSize 文件大小（字节）
 * @param filePreviewUrl 可直接访问的 removable 预览 URL（如 /api/removable/preview?token=xxx）
 * @param hasDownloadPermission 是否允许下载
 * @param hasPrintPermission 是否允许打印
 */
public static async previewRemovableOfficeFile(
  fileName: string,
  fileSize: number,
  filePreviewUrl: string,
  hasDownloadPermission: boolean = true,
  hasPrintPermission: boolean = false
): Promise<void> {
  console.log('📄 移动盘 Office 专用预览 - 文件名:', fileName);

  // 1. 查找合适的 Office 预览引擎（从系统配置读取，如 kkFileView 地址）
  const previewEngine = this.findSuitablePreviewEngine(fileName);
  if (!previewEngine) {
    console.warn('⚠️ 未找到 Office 预览引擎');
    this.showPreviewFailedMessage(fileName, '未配置 Office 预览服务');
    return;
  }

  // 2. 构造绝对 URL（确保跨域可访问）
  const absoluteFileUrl = this.buildAbsoluteUrl(filePreviewUrl);

  // 3. 【关键修复】将 fullfilename 作为原始 URL 的查询参数（kkFileView 要求！）
  //    这样 kkFileView 才能识别文件类型（即使 URL 本身无后缀）
  const urlWithFullFilename = `${absoluteFileUrl}&fullfilename=${encodeURIComponent(fileName)}`;

  console.log('🔗 带 fullfilename 的文件URL:', urlWithFullFilename);

  // 4. 伪造一个 minimal Matter（仅用于传递名称和权限，不参与 token 获取）
  const fakeMatter = {
    name: fileName,
    size: fileSize,
    privacy: false,
    uuid: '', // 不设有效 UUID
    spaceUuid: '',
    userUuid: '',
    getPreviewUrl: () => urlWithFullFilename // 返回带 fullfilename 的 URL
  } as unknown as Matter;

  // 5. 复用现有外部预览逻辑（会自动调用 BrowserPreviewer.show）
  try {
    await this.handleExternalPreviewForRemovable(
      fakeMatter,
      previewEngine,
      urlWithFullFilename, // 注意：这里也传带 fullfilename 的 URL
      hasDownloadPermission,
      hasPrintPermission
    );
  } catch (error) {
    console.error('移动盘 Office 预览失败:', error);
    this.showPreviewFailedMessage(fileName, error instanceof Error ? error.message : String(error));
  }
}
public  static async fallbackOfficePreview(
  matter: Matter,
  previewUrl?: string,
  hasDownloadPermission?: boolean,
  hasPrintPermission?: boolean
): Promise<void> {
  console.log('🔄 Office文件备选预览方案');
  
  // ✅ 修复：生成水印参数
  const watermark = await this.generateWatermarkParams(matter);
  console.log('备选预览方案水印状态:', watermark ? '已启用' : '未启用');
  
  // 尝试使用本地预览服务
  try {
    if (previewUrl) {
      // 确保是预览接口
      if (previewUrl.includes('/api/removable/download')) {
        previewUrl = previewUrl.replace('/api/removable/download', '/api/removable/preview');
      }
      
      // 构建预览URL - 模拟本地预览服务
      const params = new URLSearchParams();
      params.set('url', encodeURIComponent(previewUrl));
      params.set('fullfilename', matter.name);
      params.set('preview', 'true');
      params.set('disposition', 'inline');
      params.set('officePreviewType', 'pdf');
      params.set('type', 'pdf');
      
      const previewServiceUrl = `/onlinePreview?${params.toString()}`; // ✅ 已修复：移除空格
      console.log('预览服务URL:', previewServiceUrl);
      
      // 使用BrowserPreviewer显示，并传递水印参数
      BrowserPreviewer.show(
        matter.name,
        previewServiceUrl,
        matter.size,
        watermark, // ✅ 修复：传递水印参数
        hasDownloadPermission,
        hasPrintPermission,
        matter
      );
    }
  } catch (error) {
    console.error('Office备选预览失败:', error);
    this.showPreviewFailedMessage(matter.name, error);
  }
}
  // 新增方法：从 Matter 构建移动盘预览 URL
  private static buildRemovablePreviewUrlFromMatter(matter: Matter): string {
    if ((matter as any).isRemovable) {
      return this.buildRemovablePreviewUrl(
        (matter as any).removableDeviceId,
        (matter as any).removableFilePath,
        matter.name
      );
    }
    return matter.getPreviewUrl();
  }
  
  static async preview(
    matter: Matter, 
    previewUrl?: string, 
    hasDownloadPermission?: boolean, 
    hasPrintPermission?: boolean
): Promise<void> {
    const fileName = matter.name;
    const extension = MimeUtil.getExtensionWithoutDot(fileName);
    
    console.log('=== 预览方法调用 ===');
    console.log('文件名:', fileName);
    console.log('扩展名:', extension);
    console.log('文件UUID:', matter.uuid);
    console.log('是CAD类型:', this.isCadType(extension));
    console.log('是DXF文件:', this.isDXFType(extension));
    console.log('是DWG文件:', this.isDWGType(extension));
    console.log('是思维导图文件:', this.isMindMapType(extension));
    console.log('下载权限参数:', hasDownloadPermission);
    console.log('打印权限参数:', hasPrintPermission);
    console.log('=================');
    
    if (!extension) {
        MessageBoxUtil.warning(`${fileName} 没有后缀名，无法预览`);
        return;
    }
    
    let downloadPermission = hasDownloadPermission;
    let printPermission = hasPrintPermission;
    if (downloadPermission === undefined) {
        downloadPermission = await this.checkDownloadPermission(matter);
    }
    if (printPermission === undefined) {
        printPermission = await this.checkPrintPermission(matter);
    }
    console.log('最终权限结果:', { download: downloadPermission, print: printPermission });
    
    if (this.isMindMapType(extension)) {
        console.log('🧠 进入思维导图文件预览流程');
        const watermark = await this.generateWatermarkParams(matter);
        await this.handleMindMapPreview(matter, previewUrl, watermark, downloadPermission, printPermission);
        return;
    }
    
    if (this.isCadType(extension)) {
        console.log('🎯 进入CAD文件预览流程');
        const watermark = await this.generateWatermarkParams(matter);
        if (this.isDXFType(extension)) {
            await this.handleDXFPreview(matter, previewUrl, watermark, downloadPermission, printPermission);
        } else if (this.isDWGType(extension)) {
            await this.handleDWGConversionPreview(matter, previewUrl, watermark, downloadPermission, printPermission);
        }
        return;
    }
    
    const watermark = await this.generateWatermarkParams(matter);
    console.log('水印配置:', watermark ? '已启用' : '未启用');
    
    let finalUrl = previewUrl;
    if (!finalUrl) {
        if ((matter as any).isRemovable) {
            finalUrl = this.buildRemovablePreviewUrl(
                (matter as any).removableDeviceId,
                (matter as any).removableFilePath,
                matter.name
            );
        } else {
            finalUrl = await this.getMatterPreviewUrl(matter);
        }
    }
    
    if (this.isDirectPreviewType(extension)) {
      let finalPreviewUrl = finalUrl;
      // 修复：移动盘判断应该使用 !matter.uuid 而不是 === null（因为可能为空字符串）
      const isRemovable = matter.uuid === null || (matter as any).isRemovable === true;
      // 只有非移动盘且是图片时才重建 URL（本地文件图片可能缺少完整URL）
      if (!isRemovable && this.isImageType(extension)) {
          finalPreviewUrl = await this.getCorrectImagePreviewUrl(matter);
      }
      // 移动盘文件（包括图片）直接使用 finalUrl，无需重建
      BrowserPreviewer.show(
          matter.name,
          finalPreviewUrl,
          matter.size,
          watermark,
          downloadPermission,
          printPermission,
          matter
      );
      return;
    }
    if (this.isOfficeType(extension)) {
        console.log('📊 查找Office预览引擎');
        const previewEngine = this.findSuitablePreviewEngine(fileName);
        if (previewEngine) {
            console.log('📊 处理Office文档预览');
            this.handleOfficePreview(matter, previewEngine, finalUrl, watermark, downloadPermission, printPermission);
        } else {
            console.log('❌ 找不到合适的Office预览引擎');
            this.fallbackOfficePreview(matter, finalUrl, downloadPermission, printPermission);
        }
        return;
    }
    
    console.log('🔍 查找第三方预览引擎');
    const previewEngine = this.findSuitablePreviewEngine(fileName);
    if (previewEngine) {
        console.log('🌐 使用第三方预览引擎');
        this.handleExternalPreview(matter, previewEngine, finalUrl, downloadPermission, printPermission);
    } else {
        console.log('❌ 找不到合适的预览引擎');
        this.showPreviewFailedMessage(fileName, '不支持的文件格式或无法预览');
    }
}
  // 新增方法：获取本地文件的预览URL（带token）
  /*private static async getMatterPreviewUrl(matter: Matter): Promise<string> {
    return new Promise((resolve) => {
      if (matter.privacy) {
        // 需要token的私有文件
        const downloadToken = new DownloadToken();
        downloadToken.httpFetchDownloadToken(matter.uuid!, () => {
          resolve(matter.getPreviewUrl(downloadToken.uuid!));
        });
      } else {
        // 公开文件
        resolve(matter.getPreviewUrl());
      }
    });
  }*/
  private static async getMatterPreviewUrl(matter: Matter): Promise<string> {
    const extension = MimeUtil.getExtensionWithoutDot(matter.name)?.toLowerCase();
    const isImage = this.isImageType(extension);

    // 图片文件：直接构造标准预览 URL，避免依赖 matter.getPreviewUrl() 可能返回不完整路径
    if (isImage) {
        return new Promise((resolve) => {
            const buildImageUrl = (tokenUuid?: string) => {
                // 标准格式：/api/alien/preview/{uuid}/{encodedName}
                let url = `/api/alien/preview/${matter.uuid}/${encodeURIComponent(matter.name)}`;
                if (tokenUuid) {
                    url += `?downloadTokenUuid=${encodeURIComponent(tokenUuid)}`;
                }
                resolve(url);
            };
            if (matter.privacy) {
                const downloadToken = new DownloadToken();
                downloadToken.httpFetchDownloadToken(matter.uuid!, () => {
                    buildImageUrl(downloadToken.uuid!);
                });
            } else {
                buildImageUrl();
            }
        });
    }

    // 非图片文件（PDF、TXT、Office 等）保持原有逻辑不变
    return new Promise((resolve) => {
        if (matter.privacy) {
            const downloadToken = new DownloadToken();
            downloadToken.httpFetchDownloadToken(matter.uuid!, () => {
                resolve(matter.getPreviewUrl(downloadToken.uuid!));
            });
        } else {
            resolve(matter.getPreviewUrl());
        }
    });
}
  private static handleDirectPreview(
    matter: Matter,
    previewUrl?: string,
    watermark?: any,
    hasDownloadPermission?: boolean,
    hasPrintPermission?: boolean
  ): void {
    const processPreview = (finalUrl: string) => {
      BrowserPreviewer.show(matter.name, finalUrl, matter.size, watermark, hasDownloadPermission, hasPrintPermission, matter);
    };

    if (previewUrl) {
      processPreview(previewUrl);
    } else {
      const needToken = matter.privacy;
      this.prepareMatterUrl(matter, needToken, (url: string) => {
        processPreview(url);
      });
    }
  }

// 修改：handleOfficePreview方法，确保正确处理移动盘文件
private static handleOfficePreview(
  matter: Matter,
  engine: PreviewEngine,
  previewUrl?: string,
  watermark?: any,
  hasDownloadPermission?: boolean,
  hasPrintPermission?: boolean
): void {
  const targetUrl = engine.url;

  console.log('🎯 Office预览处理');
  console.log('文件:', matter.name);
  console.log('文件扩展名:', matter.name.split('.').pop()?.toLowerCase());

  const processPreview = (finalUrl: string) => {
    console.log('原始URL:', finalUrl);
    
    const safeDownloadPermission = hasDownloadPermission ?? false;
    const safePrintPermission = hasPrintPermission ?? false;
    const hasAnyPermission = safeDownloadPermission || safePrintPermission;
    
    console.log('权限状态:', {
      download: safeDownloadPermission,
      print: safePrintPermission,
      any: hasAnyPermission
    });
    
    const fileExt = matter.name.split('.').pop()?.toLowerCase() || '';
    const isOfficeFile = ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'wps', 'et', 'dps'].includes(fileExt);
    
    try {
      const urlObj = new URL(finalUrl);
      const params = urlObj.searchParams;
      
      const originalUrlParam = params.get('url');
      
      // 清空所有参数
      params.forEach((value, key) => {
        params.delete(key);
      });
      
      if (originalUrlParam) {
        params.set('url', originalUrlParam);
      }
      
      if (isOfficeFile) {
        params.set('officePreviewType', 'pdf');
        params.set('type', 'pdf');
        console.log('📄 设置Office转PDF参数');
      }
      
      params.set('toolbar', '1');
      params.set('office.viewState', 'show');
      
      params.set('download', safeDownloadPermission ? '1' : '0');
      params.set('print', safePrintPermission ? '1' : '0');
      
      if (hasAnyPermission) {
        console.log('✅ 有权限：开启下载或打印功能');
      } else {
        params.set('disableRightClick', 'true');
        console.log('✅ 无权限：禁用下载和打印，保留翻页功能');
      }
      
      params.set('_t', Date.now().toString());
      
      // ✅ 关键修改：不通过 params 设置 fullfilename，而是手动拼接
      const baseQuery = params.toString(); // 此时不包含 fullfilename
      const encodedFilename = encodeURIComponent(matter.name); // 空格变成 %20
      const finalQuery = baseQuery ? `${baseQuery}&fullfilename=${encodedFilename}` : `fullfilename=${encodedFilename}`;
      
      urlObj.search = finalQuery;
      finalUrl = urlObj.toString();
      
    } catch (e) {
      console.warn('URL解析失败，使用字符串处理:', e);
      
      const encodedFileUrl = finalUrl.split('?url=')[1]?.split('&')[0] || '';
      const baseUrl = finalUrl.split('?')[0];
      
      const newParams: string[] = [
        `url=${encodedFileUrl}`,
        `fullfilename=${encodeURIComponent(matter.name)}`
      ];
      
      if (isOfficeFile) {
        newParams.push('officePreviewType=pdf');
        newParams.push('type=pdf');
      }
      
      newParams.push('toolbar=1');
      newParams.push('office.viewState=show');
      
      newParams.push(`download=${safeDownloadPermission ? '1' : '0'}`);
      newParams.push(`print=${safePrintPermission ? '1' : '0'}`);
      
      if (hasAnyPermission) {
        console.log('✅ 有权限：开启下载或打印功能');
      } else {
        newParams.push('disableRightClick=true');
        console.log('✅ 无权限：禁用下载和打印，保留翻页功能');
      }
      
      newParams.push(`_t=${Date.now()}`);
      finalUrl = `${baseUrl}?${newParams.join('&')}`;
    }
    
    console.log('=======================');
    console.log('最终URL:', finalUrl);
    console.log('=======================');

    BrowserPreviewer.show(
      matter.name, 
      finalUrl, 
      matter.size, 
      watermark, 
      safeDownloadPermission,
      safePrintPermission,
      matter,
      hasAnyPermission
    );
  };

  // 如果是移动盘文件，直接使用传入的previewUrl
  if ((matter as any).isRemovable) {
    console.log('📱 移动盘文件，直接构建kkFileView URL');
    
    // 直接构建kkFileView URL，避免复杂的编码逻辑
    const kkFileViewUrl = this.buildDirectKkFileViewUrlForRemovable(
      previewUrl || '',
      matter.name,
      hasDownloadPermission,
      hasPrintPermission
    );
    
    console.log('✅ 直接构建的kkFileView URL:', kkFileViewUrl);
    processPreview(kkFileViewUrl);
  } else {
      // 本地文件，需要获取token
      this.forceFetchTokenForOffice(matter, (fileUrl: string) => {
        // ✅ 使用引擎模板替换占位符，传入文件名
        const kkFileViewUrl = this.replaceUrlPlaceholders(targetUrl, fileUrl, matter.name);
        console.log('生成的kkFileView URL:', kkFileViewUrl);
        processPreview(kkFileViewUrl);
      });

  }
}
private static buildDirectKkFileViewUrlForRemovable(
  previewUrl: string,
  fileName: string,
  hasDownloadPermission?: boolean,
  hasPrintPermission?: boolean
): string {
  try {
    console.log('🔧 直接构建移动盘kkFileView URL');
    
    // 1. 构建绝对URL
    const absoluteUrl = this.buildAbsoluteUrl(previewUrl);
    console.log('🔗 绝对URL（前100字符）:', absoluteUrl.substring(0, 100) + '...');
    
    // 2. Base64编码（与本地文件保持一致）
    const base64Encoded = this.base64EncodeUrl(absoluteUrl);
    console.log('🔐 Base64编码（前50字符）:', base64Encoded.substring(0, 50) + '...');
    
    // 3. 构建kkFileView参数
    const params = new URLSearchParams({
      url: base64Encoded,
      fullfilename: fileName,
      preview: 'true',
      disposition: 'inline',
      _t: Date.now().toString()
    });
    
    // 4. 添加Office相关参数
    const extension = fileName.split('.').pop()?.toLowerCase() || '';
    if (extension === 'ppt' || extension === 'pptx') {
      params.set('type', 'ppt');
      params.set('officePreviewType', 'pdf');
    } else if (extension === 'doc' || extension === 'docx') {
      params.set('type', 'doc');
      params.set('officePreviewType', 'pdf');
    } else if (extension === 'xls' || extension === 'xlsx') {
      params.set('type', 'excel');
      params.set('officePreviewType', 'pdf');
    }
    
    // 5. 添加权限参数
    if (hasDownloadPermission !== undefined) {
      params.set('download', hasDownloadPermission ? '1' : '0');
    }
    if (hasPrintPermission !== undefined) {
      params.set('print', hasPrintPermission ? '1' : '0');
    }
    
    const kkUrl = `/onlinePreview?${params.toString()}`; // ✅ 已修复：移除空格
    console.log('✅ 最终的kkFileView URL（前200字符）:', kkUrl.substring(0, 200) + '...');
    
    return kkUrl;
    
  } catch (error) {
    console.error('构建直接kkFileView URL失败:', error);
    
    // 备用方案：最简单的参数
    const params = new URLSearchParams({
      url: encodeURIComponent(previewUrl),
      fullfilename: encodeURIComponent(fileName),
      preview: 'true'
    });
    return `/onlinePreview?${params.toString()}`; // ✅ 已修复：移除空格
  }
}
// 新增：Base64编码URL（与本地文件保持一致）
private static base64EncodeUrl(url: string): string {
  try {
    // 关键：只进行一次Base64编码，不进行URL编码
    // 本地文件的做法：直接对URL进行Base64编码
    const base64 = btoa(encodeURIComponent(url));
    console.log('🔐 Base64编码结果长度:', base64.length);
    console.log('🔐 Base64编码（前50字符）:', base64.substring(0, 50));
    return base64;
  } catch (error) {
    console.error('Base64编码失败:', error);
    // 备用方案
    return btoa(url);
  }
}
  private static forceFetchTokenForOffice(matter: Matter, callback: (url: string) => void): void {
    console.log('🔄 强制为Office文件获取token，文件UUID:', matter.uuid);
    
    const downloadToken = new DownloadToken();
    
    downloadToken.httpFetchDownloadToken(matter.uuid!, 
      () => {
        if (downloadToken.uuid) {
          const url = matter.getPreviewUrl(downloadToken.uuid);
          console.log('✅ 成功获取带token的预览URL:', url);
          callback(url);
        } else { 
          console.error('❌ 获取token失败，downloadToken.uuid为空');
          
          const fallbackUrl = matter.getPreviewUrl();
          console.log('⚠️ 使用降级方案 - 无tokenURL:', fallbackUrl);
          callback(fallbackUrl);
        }
      },
      () => {
        console.error('❌ 获取token请求失败');
        
        const fallbackUrl = matter.getPreviewUrl();
        console.log('⚠️ 错误回调中使用降级方案 - 无tokenURL:', fallbackUrl);
        callback(fallbackUrl);
      }
    );
  }

  private static handleExternalPreview(
    matter: Matter,
    engine: PreviewEngine,
    previewUrl?: string,
    hasDownloadPermission?: boolean,
    hasPrintPermission?: boolean
  ): void {
    let targetUrl = engine.url;

    const processPreview = (finalUrl: string) => {
      if (engine.previewInSite) {
        BrowserPreviewer.show(matter.name, finalUrl, matter.size, undefined, hasDownloadPermission, hasPrintPermission, matter);
      } else {
        window.open(finalUrl);
      }
    };

    if (previewUrl) {
      targetUrl = this.replaceUrlPlaceholders(targetUrl, previewUrl, matter.name);
      processPreview(targetUrl);
    } else {
      const needToken = matter.privacy && 
        (targetUrl.includes('{url}') || targetUrl.includes('{b64Url}'));
      
      this.prepareMatterUrl(matter, needToken, (url: string) => {
        const finalUrl = this.replaceUrlPlaceholders(targetUrl, url, matter.name);
        processPreview(finalUrl);
      });
    }
  }

  private static async handleMindMapPreview(
    matter: Matter, 
    previewUrl?: string, 
    watermark?: any,
    hasDownloadPermission?: boolean,
    hasPrintPermission?: boolean
  ): Promise<void> {
    console.log('🧠 处理思维导图预览');
    
    const processPreview = (finalUrl: string) => {
      console.log('🎯 使用思维导图预览组件，文件URL:', finalUrl);
      this.loadMindMapPreviewComponent(matter, finalUrl, watermark, hasDownloadPermission, hasPrintPermission);
    };

    if (previewUrl) {
      processPreview(previewUrl);
    } else {
      const needToken = matter.privacy;
      this.prepareMatterUrl(matter, needToken, (url: string) => {
        processPreview(url);
      });
    }
  }

  private static async handleDXFPreview(
    matter: Matter, 
    previewUrl?: string, 
    watermark?: any,
    hasDownloadPermission?: boolean,
    hasPrintPermission?: boolean
  ): Promise<void> {
    console.log('🔄 处理DXF预览 - React组件方式');
    
    const processPreview = (finalUrl: string) => {
      console.log('🎯 使用React组件预览DXF，文件URL:', finalUrl);
      this.loadDXFPreviewComponent(matter, finalUrl, watermark, hasDownloadPermission, hasPrintPermission);
    };

    if (previewUrl) {
      processPreview(previewUrl);
    } else {
      const needToken = matter.privacy;
      this.prepareMatterUrl(matter, needToken, (url: string) => {
        processPreview(url);
      });
    }
  }

  private static async handleDWGConversionPreview(
    matter: Matter, 
    previewUrl?: string, 
    watermark?: any,
    hasDownloadPermission?: boolean,
    hasPrintPermission?: boolean
  ): Promise<void> {
    console.log('🔄 处理DWG转换预览');
    
    const processPreview = (finalUrl: string) => {
      console.log('🎯 生成DWG转换预览页面，文件URL:', finalUrl);
      this.loadDXFPreviewComponent(matter, finalUrl, watermark, hasDownloadPermission, hasPrintPermission);
    };

    if (previewUrl) {
      processPreview(previewUrl);
    } else {
      const needToken = matter.privacy;
      this.prepareMatterUrl(matter, needToken, (url: string) => {
        processPreview(url);
      });
    }
  }

  private static async loadDXFPreviewComponent(
    matter: Matter, 
    fileUrl: string, 
    watermark?: any,
    hasDownloadPermission?: boolean,
    hasPrintPermission?: boolean
  ): Promise<void> {
    try {
      console.log('🔄 加载DXF预览组件');

      const previewContainer = document.createElement('div');
      previewContainer.id = 'dxf-preview-container';
      previewContainer.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        z-index: 10000;
        background: #1e1e1e;
      `;
      document.body.appendChild(previewContainer);

      const DxfPreviewComponent: React.FC = () => {
        const containerRef = React.useRef<HTMLDivElement>(null);
        const [loading, setLoading] = React.useState(true);
        const [error, setError] = React.useState<string>('');
        const [errorData, setErrorData] = React.useState<any>(null);
        const [stats, setStats] = React.useState({ processed: 0, skipped: 0, total: 0 });
        const [zoom, setZoom] = React.useState(1);
        const [viewMode, setViewMode] = React.useState<'pan' | 'orbit'>('pan');
        
        const sceneRef = React.useRef<THREE.Scene | null>(null);
        const cameraRef = React.useRef<THREE.PerspectiveCamera | null>(null);
        const rendererRef = React.useRef<THREE.WebGLRenderer | null>(null);
        const controlsRef = React.useRef<OrbitControls | null>(null);
        const animationIdRef = React.useRef<number | null>(null);

        const handleClose = () => {
          if (animationIdRef.current) {
            cancelAnimationFrame(animationIdRef.current);
          }
          if (sceneRef.current) {
            sceneRef.current.traverse((object: any) => {
              if (object.geometry) object.geometry.dispose();
              if (object.material) object.material.dispose();
            });
          }
          if (rendererRef.current) {
            rendererRef.current.dispose();
          }
          if (document.body.contains(previewContainer)) {
            ReactDOM.unmountComponentAtNode(previewContainer);
            document.body.removeChild(previewContainer);
          }
        };

        const handleZoomIn = () => {
          if (cameraRef.current) {
            cameraRef.current.zoom = Math.min(cameraRef.current.zoom * 1.2, 100);
            cameraRef.current.updateProjectionMatrix();
            setZoom(cameraRef.current.zoom);
          }
        };

        const handleZoomOut = () => {
          if (cameraRef.current) {
            cameraRef.current.zoom = Math.max(cameraRef.current.zoom / 1.2, 0.01);
            cameraRef.current.updateProjectionMatrix();
            setZoom(cameraRef.current.zoom);
          }
        };

        const handleResetZoom = () => {
          if (cameraRef.current) {
            cameraRef.current.zoom = 1;
            cameraRef.current.updateProjectionMatrix();
            if (controlsRef.current) {
              controlsRef.current.update();
            }
            setZoom(1);
          }
        };

        const handleToggleViewMode = () => {
          const newMode = viewMode === 'pan' ? 'orbit' : 'pan';
          setViewMode(newMode);
          if (controlsRef.current) {
            controlsRef.current.enableRotate = newMode === 'orbit';
            controlsRef.current.enablePan = true;
            controlsRef.current.enableZoom = true;
          }
        };

        const renderWatermark = () => {
          if (!watermark) return null;
          
          const { text, fontSize, color, opacity, rotate } = watermark;
          
          const watermarks = [];
          const rows = 6;
          const cols = 6;
          const watermarkWidth = 400;
          const watermarkHeight = 200;
          
          for (let i = 0; i < rows; i++) {
            for (let j = 0; j < cols; j++) {
              watermarks.push(React.createElement('div', {
                key: `watermark-${i}-${j}`,
                style: {
                  position: 'absolute',
                  left: `${j * watermarkWidth}px`,
                  top: `${i * watermarkHeight}px`,
                  width: `${watermarkWidth}px`,
                  height: `${watermarkHeight}px`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: color,
                  fontSize: `${fontSize}px`,
                  fontWeight: 'bold',
                  opacity: opacity,
                  transform: `rotate(${rotate}deg)`,
                  pointerEvents: 'none',
                  userSelect: 'none',
                  whiteSpace: 'nowrap',
                  zIndex: 1000
                }
              }, text));
            }
          }
          
          return React.createElement('div', {
            key: 'watermark-container',
            style: {
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              pointerEvents: 'none',
              overflow: 'hidden',
              zIndex: 999
            }
          }, watermarks);
        };

        React.useEffect(() => {
          const initThreeJS = async () => {
            try {
              if (!containerRef.current) return;
              
              setLoading(true);
              
              try {
                const response = await fetch(fileUrl);
                if (!response.ok) {
                  const contentType = response.headers.get('content-type');
                  if (contentType && contentType.includes('application/json')) {
                    const errorJson = await response.json();
                    setErrorData(errorJson);
                    throw new Error(errorJson.errorMessage || `文件加载失败: ${response.status} ${response.statusText}`);
                  } else {
                    throw new Error(`文件加载失败: ${response.status} ${response.statusText}`);
                  }
                }
                
                const dxfText = await response.text();
                console.log('DXF文件加载成功，长度:', dxfText.length);

                if (!dxfText || dxfText.trim().length === 0) {
                  throw new Error('DXF文件内容为空');
                }

                try {
                  const possibleJson = JSON.parse(dxfText);
                  if (possibleJson.success === false && possibleJson.errorType === 'CAD_CONVERSION_FAILED') {
                    setErrorData(possibleJson);
                    throw new Error(possibleJson.errorMessage || 'DWG文件转换失败');
                  }
                } catch (e) {
                  // 不是JSON，继续正常处理
                }

                const dxf = await PreviewerHelper.parseDXFFile(dxfText);
                
                if (!dxf) {
                  throw new Error('DXF文件解析失败');
                }

                console.log('DXF解析完成，实体数量:', dxf.entities?.length || 0);
                setStats(prev => ({ ...prev, total: dxf.entities?.length || 0 }));

                const container = containerRef.current;
                const width = container.clientWidth;
                const height = container.clientHeight;

                const scene = new THREE.Scene();
                scene.background = new THREE.Color(0x1e1e1e);
                sceneRef.current = scene;

                const aspect = width / height;
                const camera = new THREE.PerspectiveCamera(60, aspect, 0.1, 10000);
                camera.position.set(0, 0, 100);
                camera.lookAt(0, 0, 0);
                camera.zoom = 1;
                camera.updateProjectionMatrix();
                cameraRef.current = camera;

                const renderer = new THREE.WebGLRenderer({ 
                  antialias: true,
                  preserveDrawingBuffer: true 
                });
                renderer.setSize(width, height);
                renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
                renderer.setClearColor(0x1e1e1e, 1);
                container.appendChild(renderer.domElement);
                rendererRef.current = renderer;

                const controls = new OrbitControls(camera, renderer.domElement);
                controls.enableDamping = true;
                controls.dampingFactor = 0.25;
                controls.enableZoom = true;
                controls.enablePan = true;
                controls.enableRotate = false;
                controls.screenSpacePanning = true;
                controls.minDistance = 1;
                controls.maxDistance = 10000;
                controls.zoomSpeed = 1.5;
                controls.panSpeed = 2.0;
                controls.mouseButtons = {
                  LEFT: THREE.MOUSE.PAN,
                  MIDDLE: THREE.MOUSE.DOLLY,
                  RIGHT: THREE.MOUSE.ROTATE
                };
                controlsRef.current = controls;

                const ambientLight = new THREE.AmbientLight(0xffffff, 1.0);
                scene.add(ambientLight);

                const dxfGroup = PreviewerHelper.createDXFGeometryFromEntities(dxf.entities || []);
                scene.add(dxfGroup);

                const autoFitView = () => {
                  try {
                    const box = new THREE.Box3().setFromObject(dxfGroup);
                    const size = box.getSize(new THREE.Vector3());
                    const center = box.getCenter(new THREE.Vector3());

                    const maxDim = Math.max(size.x, size.y, size.z, 1);
                    const distance = maxDim * 2;
                    
                    camera.position.set(center.x, center.y, distance);
                    camera.lookAt(center.x, center.y, 0);
                    
                    controls.target.set(center.x, center.y, 0);
                    controls.update();
                    
                  } catch (err) {
                    console.warn('自动调整视图失败:', err);
                  }
                };

                autoFitView();

                const animate = () => {
                  animationIdRef.current = requestAnimationFrame(animate);
                  if (controlsRef.current) controlsRef.current.update();
                  if (rendererRef.current && sceneRef.current && cameraRef.current) {
                    rendererRef.current.render(sceneRef.current, cameraRef.current);
                  }
                };
                animate();

                setLoading(false);
                
              } catch (err) {
                console.error('CAD预览失败:', err);
                setError(err instanceof Error ? err.message : '未知错误');
                setLoading(false);
              }
              
            } catch (err) {
              console.error('ThreeJS初始化失败:', err);
              setError(err instanceof Error ? err.message : '未知错误');
              setLoading(false);
            }
          };
          
          initThreeJS();
          
          const handleResize = () => {
            if (containerRef.current && cameraRef.current && rendererRef.current) {
              const width = containerRef.current.clientWidth;
              const height = containerRef.current.clientHeight;
              
              cameraRef.current.aspect = width / height;
              cameraRef.current.updateProjectionMatrix();
              
              rendererRef.current.setSize(width, height);
            }
          };
          
          window.addEventListener('resize', handleResize);
          
          const handleKeyDown = (e: KeyboardEvent) => {
            if (!containerRef.current) return;
            
            if (!containerRef.current.contains(e.target as Node)) return;
            
            switch (e.key) {
              case '+':
              case '=':
                e.preventDefault();
                handleZoomIn();
                break;
              case '-':
              case '_':
                e.preventDefault();
                handleZoomOut();
                break;
              case '0':
                e.preventDefault();
                handleResetZoom();
                break;
              case 'r':
              case 'R':
                e.preventDefault();
                handleToggleViewMode();
                break;
              case 'Escape':
                e.preventDefault();
                handleClose();
                break;
            }
          };
          
          window.addEventListener('keydown', handleKeyDown);
          
          return () => {
            if (animationIdRef.current) {
              cancelAnimationFrame(animationIdRef.current);
            }
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('keydown', handleKeyDown);
          };
        }, [fileUrl]);

        React.useEffect(() => {
          if (controlsRef.current) {
            controlsRef.current.enableRotate = viewMode === 'orbit';
            controlsRef.current.update();
          }
        }, [viewMode]);

        return React.createElement('div', {
          style: { 
            width: '100%', 
            height: '100%', 
            background: '#1e1e1e',
            display: 'flex',
            flexDirection: 'column'
          }
        }, [
          React.createElement('div', {
            key: 'header',
            style: {
              background: '#2d2d30',
              padding: '15px 20px',
              borderBottom: '1px solid #3e3e42',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              color: 'white'
            }
          }, [
            React.createElement('div', {
              key: 'title',
              style: { display: 'flex', alignItems: 'center', gap: '15px' }
            }, [
              React.createElement('strong', { key: 'text' }, '📐 CAD二维图纸预览'),
              React.createElement('div', {
                key: 'file-info',
                style: {
                  background: 'rgba(255,255,255,0.05)',
                  padding: '10px 15px',
                  borderRadius: '4px',
                  fontSize: '12px'
                }
              }, [
                React.createElement('strong', { key: 'name' }, matter.name),
                React.createElement('span', {
                  key: 'badge',
                  style: {
                    background: '#0e639c',
                    padding: '2px 8px',
                    borderRadius: '12px',
                    fontSize: '11px',
                    marginLeft: '8px'
                  }
                }, '2D CAD')
              ]),
              !error && React.createElement('div', {
                key: 'stats',
                style: {
                  background: 'rgba(76, 175, 80, 0.2)',
                  padding: '8px 12px',
                  borderRadius: '4px',
                  fontSize: '11px',
                  border: '1px solid rgba(76, 175, 80, 0.3)'
                }
              }, `实体: ${stats.processed}/${stats.total}`)
            ]),
            React.createElement('div', {
              key: 'controls',
              style: { display: 'flex', gap: '10px', alignItems: 'center' }
            }, [
              React.createElement('div', {
                key: 'zoom-controls',
                style: {
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                  background: '#2d2d30',
                  padding: '4px',
                  borderRadius: '6px',
                  marginRight: '10px',
                  border: '1px solid #3e3e42'
                }
              }, [
                React.createElement('button', {
                  key: 'zoom-out',
                  onClick: handleZoomOut,
                  style: {
                    background: 'none',
                    border: 'none',
                    padding: '6px 10px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    color: 'white',
                    opacity: zoom <= 0.01 ? 0.5 : 1
                  },
                  disabled: zoom <= 0.01,
                  title: '缩小 (快捷键: -)'
                }, '−'),
                React.createElement('span', {
                  key: 'zoom-level',
                  style: { 
                    fontSize: '12px', 
                    minWidth: '40px', 
                    textAlign: 'center',
                    fontWeight: 'bold',
                    color: 'white'
                  }
                }, Math.round(zoom * 100) + '%'),
                React.createElement('button', {
                  key: 'zoom-in',
                  onClick: handleZoomIn,
                  style: {
                    background: 'none',
                    border: 'none',
                    padding: '6px 10px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    color: 'white',
                    opacity: zoom >= 100 ? 0.5 : 1
                  },
                  disabled: zoom >= 100,
                  title: '放大 (快捷键: +)'
                }, '+'),
                React.createElement('button', {
                  key: 'reset-zoom',
                  onClick: handleResetZoom,
                  style: {
                    background: '#4a90e2',
                    color: 'white',
                    border: 'none',
                    padding: '6px 10px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    marginLeft: '5px',
                    fontSize: '12px'
                  },
                  title: '重置缩放 (快捷键: 0)'
                }, '重置')
              ]),
              
              React.createElement('button', {
                key: 'view-mode-toggle',
                onClick: handleToggleViewMode,
                style: {
                  background: viewMode === 'pan' ? '#4a90e2' : '#ff9800',
                  color: 'white',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px'
                },
                title: '切换视图模式 (快捷键: R)'
              }, [
                React.createElement('span', { key: 'icon' }, viewMode === 'pan' ? '↔️' : '🔄'),
                React.createElement('span', { key: 'text' }, viewMode === 'pan' ? '平移模式' : '旋转模式')
              ]),
              
              React.createElement('div', {
                key: 'hint',
                style: {
                  background: 'rgba(255,255,255,0.05)',
                  padding: '8px 12px',
                  borderRadius: '4px',
                  fontSize: '11px',
                  color: '#aaa',
                  marginLeft: '10px'
                }
              }, [
                React.createElement('div', { key: 'line1' }, '鼠标拖拽: 平移视图'),
                React.createElement('div', { key: 'line2' }, '滚轮: 缩放视图')
              ]),
              
              React.createElement('button', {
                key: 'close-btn',
                onClick: handleClose,
                style: {
                  background: '#f44336',
                  color: 'white',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '12px'
                },
                title: '关闭 (快捷键: Esc)'
              }, '关闭')
            ])
          ]),
          
          React.createElement('div', {
            key: 'content',
            ref: containerRef,
            style: { 
              flex: 1, 
              position: 'relative', 
              background: '#1e1e1e',
              width: '100%',
              height: '100%',
              overflow: 'hidden'
            }
          }, [
            loading && React.createElement('div', {
              key: 'loading',
              style: {
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                background: 'rgba(0,0,0,0.9)',
                padding: '30px',
                borderRadius: '10px',
                textAlign: 'center',
                color: 'white',
                zIndex: 1001
              }
            }, [
              React.createElement('div', {
                key: 'icon',
                style: { fontSize: '48px', marginBottom: '20px' }
              }, '🔄'),
              React.createElement('h3', { key: 'title' }, '正在加载CAD图纸...'),
              stats.total > 0 && React.createElement('p', { 
                key: 'progress',
                style: { marginTop: '10px', fontSize: '14px', opacity: 0.8 }
              }, `处理实体: ${stats.processed}/${stats.total}`),
              React.createElement('div', {
                key: 'controls-hint',
                style: { 
                  marginTop: '20px', 
                  padding: '10px', 
                  background: 'rgba(255,255,255,0.1)',
                  borderRadius: '6px',
                  fontSize: '12px',
                  textAlign: 'left'
                }
              }, [
                React.createElement('p', { key: 'p1' }, '📌 操作提示:'),
                React.createElement('ul', { 
                  key: 'list',
                  style: { margin: '5px 0 0 15px', padding: 0 }
                }, [
                  React.createElement('li', { key: 'l1' }, '鼠标拖拽: 平移视图'),
                  React.createElement('li', { key: 'l2' }, '鼠标滚轮: 缩放视图'),
                  React.createElement('li', { key: 'l3' }, 'R键: 切换平移/旋转模式'),
                  React.createElement('li', { key: 'l4' }, '+/-键: 放大/缩小'),
                  React.createElement('li', { key: 'l5' }, '0键: 重置视图')
                ])
              ])
            ]),
            
            error && React.createElement('div', {
              key: 'error',
              style: {
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                background: 'rgba(139, 0, 0, 0.9)',
                padding: '30px',
                borderRadius: '10px',
                textAlign: 'center',
                color: 'white',
                zIndex: 1001,
                maxWidth: '500px'
              }
            }, [
              React.createElement('div', {
                key: 'icon',
                style: { fontSize: '48px', marginBottom: '20px' }
              }, '❌'),
              React.createElement('h3', { key: 'title' }, '预览失败'),
              React.createElement('p', { 
                key: 'message',
                style: { margin: '0 0 20px 0', lineHeight: '1.5' }
              }, error),
              React.createElement('div', {
                key: 'buttons',
                style: { display: 'flex', gap: '10px', justifyContent: 'center' }
              }, [
                React.createElement('button', {
                  key: 'close-btn',
                  onClick: handleClose,
                  style: {
                    background: '#f44336',
                    color: 'white',
                    border: 'none',
                    padding: '8px 16px',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }
                }, '关闭')
              ])
            ]),
            
            renderWatermark(),
            
            !loading && !error && React.createElement('div', {
              key: 'bottom-hint',
              style: {
                position: 'absolute',
                bottom: '20px',
                left: '50%',
                transform: 'translateX(-50%)',
                background: 'rgba(0, 0, 0, 0.7)',
                padding: '10px 15px',
                borderRadius: '6px',
                fontSize: '11px',
                color: '#ddd',
                zIndex: 998,
                display: 'flex',
                gap: '15px',
                alignItems: 'center',
                backdropFilter: 'blur(4px)'
              }
            }, [
              React.createElement('div', { 
                key: 'mode',
                style: { display: 'flex', alignItems: 'center', gap: '5px' }
              }, [
                React.createElement('span', { key: 'icon' }, viewMode === 'pan' ? '↔️' : '🔄'),
                React.createElement('span', { key: 'text' }, viewMode === 'pan' ? '平移模式' : '旋转模式')
              ]),
              React.createElement('div', { 
                key: 'zoom',
                style: { display: 'flex', alignItems: 'center', gap: '5px' }
              }, [
                React.createElement('span', { key: 'icon' }, '🔍'),
                React.createElement('span', { key: 'text' }, `缩放: ${Math.round(zoom * 100)}%`)
              ]),
              React.createElement('div', { 
                key: 'hint-text',
                style: { 
                  borderLeft: '1px solid rgba(255,255,255,0.3)', 
                  paddingLeft: '10px',
                  fontStyle: 'italic'
                }
              }, '使用鼠标拖拽平移，滚轮缩放')
            ])
          ])
        ]);
      };

      ReactDOM.render(React.createElement(DxfPreviewComponent), previewContainer);

    } catch (error) {
      console.error('加载CAD预览组件失败:', error);
      MessageBoxUtil.error('CAD预览组件加载失败: ' + (error instanceof Error ? error.message : '未知错误'));
    }
  }

  private static async loadMindMapPreviewComponent(
    matter: Matter, 
    fileUrl: string, 
    watermark?: any,
    hasDownloadPermission?: boolean,
    hasPrintPermission?: boolean
  ): Promise<void> {
    try {
      console.log('🔄 加载思维导图预览组件');

      const previewContainer = document.createElement('div');
      previewContainer.id = 'mindmap-preview-container';
      previewContainer.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        z-index: 10000;
        background: #f5f5f7;
      `;
      document.body.appendChild(previewContainer);

      const MindMapPreviewComponent: React.FC = () => {
        const containerRef = React.useRef<HTMLDivElement>(null);
        const [loading, setLoading] = React.useState(true);
        const [error, setError] = React.useState<string>('');
        const [mindMapData, setMindMapData] = React.useState<any>(null);
        const [zoom, setZoom] = React.useState(1);
        const [fileInfo] = React.useState({
          name: matter.name,
          size: matter.size,
          extension: matter.name.split('.').pop()?.toLowerCase() || ''
        });

        const handleClose = () => {
          if (document.body.contains(previewContainer)) {
            ReactDOM.unmountComponentAtNode(previewContainer);
            document.body.removeChild(previewContainer);
          }
        };

        const handleZoomIn = () => {
          setZoom(prev => Math.min(prev + 0.1, 2));
        };

        const handleZoomOut = () => {
          setZoom(prev => Math.max(prev - 0.1, 0.5));
        };

        const handleResetZoom = () => {
          setZoom(1);
        };

        const renderWatermark = () => {
          if (!watermark) return null;
          
          const { text, fontSize, color, opacity, rotate } = watermark;
          
          const watermarks = [];
          const rows = 8;
          const cols = 8;
          const watermarkSize = 300;
          
          for (let i = 0; i < rows; i++) {
            for (let j = 0; j < cols; j++) {
              watermarks.push(
                React.createElement('div', {
                  key: `watermark-${i}-${j}`,
                  style: {
                    position: 'absolute',
                    left: `${j * watermarkSize}px`,
                    top: `${i * watermarkSize}px`,
                    width: `${watermarkSize}px`,
                    height: `${watermarkSize}px`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: color,
                    fontSize: `${fontSize}px`,
                    fontWeight: 'bold',
                    opacity: opacity,
                    transform: `rotate(${rotate}deg)`,
                    pointerEvents: 'none',
                    userSelect: 'none',
                    whiteSpace: 'nowrap',
                    zIndex: 9998
                  }
                }, text)
              );
            }
          }
          
          return React.createElement('div', {
            key: 'watermark-container',
            style: {
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              pointerEvents: 'none',
              overflow: 'hidden',
              zIndex: 9997
            }
          }, watermarks);
        };

        const renderMindMapNode = (node: any, depth: number = 0, index: number = 0) => {
          if (!node) return null;
          
          const nodeId = `node-${depth}-${index}`;
          const hasChildren = node.children && node.children.length > 0;
          
          const colors = [
            '#4a90e2', '#50c878', '#ff6b6b', '#ffa726', '#ab47bc', '#26c6da', '#66bb6a', '#ff7043'
          ];
          const colorIndex = depth % colors.length;
          const nodeColor = depth === 0 ? colors[0] : colors[colorIndex];
          const textColor = depth === 0 ? 'white' : '#333';
          const borderColor = depth === 0 ? 'transparent' : `rgba(0,0,0,0.1)`;
          
          return React.createElement('div', {
            key: nodeId,
            className: `mindmap-node depth-${depth}`,
            style: {
              position: 'relative',
              padding: depth === 0 ? '12px 24px' : '8px 16px',
              margin: '6px',
              background: nodeColor,
              color: textColor,
              borderRadius: '12px',
              border: `1px solid ${borderColor}`,
              boxShadow: depth === 0 ? '0 4px 12px rgba(74, 144, 226, 0.3)' : '0 2px 6px rgba(0,0,0,0.1)',
              minWidth: depth === 0 ? '180px' : '140px',
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              transform: `scale(${zoom})`,
              transformOrigin: 'center',
              fontSize: depth === 0 ? '16px' : '14px',
              fontWeight: depth === 0 ? 'bold' : 'normal',
              zIndex: 10 - depth
            },
            onMouseEnter: (e: React.MouseEvent<HTMLDivElement>) => {
              (e.currentTarget as HTMLDivElement).style.transform = `scale(${zoom * 1.05})`;
              (e.currentTarget as HTMLDivElement).style.boxShadow = '0 6px 20px rgba(0,0,0,0.15)';
              (e.currentTarget as HTMLDivElement).style.zIndex = '100';
            },
            onMouseLeave: (e: React.MouseEvent<HTMLDivElement>) => {
              (e.currentTarget as HTMLDivElement).style.transform = `scale(${zoom})`;
              (e.currentTarget as HTMLDivElement).style.boxShadow = depth === 0 ? '0 4px 12px rgba(74, 144, 226, 0.3)' : '0 2px 6px rgba(0,0,0,0.1)';
              (e.currentTarget as HTMLDivElement).style.zIndex = '10';
            }
          }, [
            React.createElement('div', {
              key: 'text',
              style: {
                fontWeight: depth === 0 ? 'bold' : 'normal',
                fontSize: depth === 0 ? '18px' : '14px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                padding: '4px 0'
              }
            }, node.name || '未命名节点'),
            
            hasChildren && React.createElement('div', {
              key: 'children',
              style: {
                display: 'flex',
                flexDirection: 'row',
                flexWrap: 'wrap',
                justifyContent: 'center',
                alignItems: 'flex-start',
                marginTop: '15px',
                gap: '15px',
                position: 'relative'
              }
            }, node.children.map((child: any, childIndex: number) => 
              renderMindMapNode(child, depth + 1, childIndex)
            ))
          ]);
        };

        React.useEffect(() => {
          const loadMindMap = async () => {
            try {
              setLoading(true);
              setError('');

              console.log('开始加载思维导图文件:', fileUrl);
              
              const response = await fetch(fileUrl);
              if (!response.ok) {
                throw new Error(`文件加载失败: ${response.status} ${response.statusText}`);
              }
              
              const fileContent = await response.text();
              console.log('思维导图文件加载成功，字符数:', fileContent.length);

              if (!fileContent || fileContent.trim().length === 0) {
                throw new Error('思维导图文件内容为空');
              }

              const extension = matter.name.split('.').pop()?.toLowerCase() || '';
              const parsedData = PreviewerHelper.parseMindMapFile(fileContent, extension, matter);
              
              setMindMapData(parsedData);
              setLoading(false);

            } catch (err) {
              console.error('思维导图预览失败:', err);
              setError(err instanceof Error ? err.message : '未知错误');
              setLoading(false);
              
              setMindMapData({
                name: matter.name.replace(/\.[^/.]+$/, ""),
                children: [
                  { 
                    name: '文件加载失败', 
                    children: [
                      { name: '错误: ' + (err instanceof Error ? err.message : '未知错误'), children: [] },
                      { name: '文件格式: ' + fileInfo.extension, children: [] },
                      { name: '文件大小: ' + PreviewerHelper.formatFileSize(fileInfo.size), children: [] }
                    ]
                  }
                ]
              });
            }
          };

          loadMindMap();
        }, [fileUrl]);

        return React.createElement('div', {
          style: { 
            width: '100%', 
            height: '100%', 
            background: '#f5f5f7',
            display: 'flex',
            flexDirection: 'column',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
          }
        }, [
          React.createElement('div', {
            key: 'header',
            style: {
              background: '#ffffff',
              padding: '12px 20px',
              borderBottom: '1px solid #e0e0e0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }
          }, [
            React.createElement('div', {
              key: 'title',
              style: { display: 'flex', alignItems: 'center', gap: '15px' }
            }, [
              React.createElement('div', {
                key: 'icon',
                style: { fontSize: '24px' }
              }, '🧠'),
              React.createElement('div', { 
                key: 'text',
                style: { display: 'flex', flexDirection: 'column' }
              }, [
                React.createElement('strong', { 
                  key: 'title-text',
                  style: { fontSize: '16px', color: '#333' }
                }, '思维导图预览'),
                React.createElement('div', {
                  key: 'file-info',
                  style: {
                    background: 'rgba(74, 144, 226, 0.1)',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    fontSize: '11px',
                    color: '#4a90e2',
                    marginTop: '4px'
                  }
                }, [
                  React.createElement('span', { key: 'name' }, fileInfo.name),
                  React.createElement('span', {
                    key: 'size',
                    style: { marginLeft: '8px', opacity: 0.7 }
                  }, `(${PreviewerHelper.formatFileSize(fileInfo.size)})`),
                  React.createElement('span', {
                    key: 'format',
                    style: { 
                      marginLeft: '8px', 
                      background: '#e0e0e0', 
                      padding: '1px 6px', 
                      borderRadius: '3px',
                      fontSize: '10px'
                    }
                  }, `.${fileInfo.extension}`)
                ])
              ])
            ]),
            React.createElement('div', {
              key: 'controls',
              style: { display: 'flex', gap: '10px', alignItems: 'center' }
            }, [
              React.createElement('div', {
                key: 'zoom-controls',
                style: {
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                  background: '#f8f9fa',
                  padding: '4px',
                  borderRadius: '6px',
                  marginRight: '10px',
                  border: '1px solid #e0e0e0'
                }
              }, [
                React.createElement('button', {
                  key: 'zoom-out',
                  onClick: handleZoomOut,
                  disabled: zoom <= 0.5,
                  style: {
                    background: 'none',
                    border: 'none',
                    padding: '6px 10px',
                    borderRadius: '4px',
                    cursor: zoom <= 0.5 ? 'not-allowed' : 'pointer',
                    opacity: zoom <= 0.5 ? 0.5 : 1,
                    fontSize: '14px'
                  },
                  title: '缩小'
                }, '−'),
                React.createElement('span', {
                  key: 'zoom-level',
                  style: { 
                    fontSize: '12px', 
                    minWidth: '40px', 
                    textAlign: 'center',
                    fontWeight: 'bold',
                    color: '#333'
                  }
                }, Math.round(zoom * 100) + '%'),
                React.createElement('button', {
                  key: 'zoom-in',
                  onClick: handleZoomIn,
                  disabled: zoom >= 2,
                  style: {
                    background: 'none',
                    border: 'none',
                    padding: '6px 10px',
                    borderRadius: '4px',
                    cursor: zoom >= 2 ? 'not-allowed' : 'pointer',
                    opacity: zoom >= 2 ? 0.5 : 1,
                    fontSize: '14px'
                  },
                  title: '放大'
                }, '+'),
                React.createElement('button', {
                  key: 'reset-zoom',
                  onClick: handleResetZoom,
                  style: {
                    background: '#4a90e2',
                    color: 'white',
                    border: 'none',
                    padding: '6px 10px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    marginLeft: '5px',
                    fontSize: '12px'
                  },
                  title: '重置缩放'
                }, '重置')
              ]),
              
              React.createElement('button', {
                key: 'close-btn',
                onClick: handleClose,
                style: {
                  background: '#dc3545',
                  color: 'white',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: 'bold'
                }
              }, '关闭')
            ])
          ]),
          
          React.createElement('div', {
            key: 'content',
            ref: containerRef,
            style: { 
              flex: 1, 
              position: 'relative', 
              background: '#f5f5f7',
              width: '100%',
              height: '100%',
              overflow: 'auto',
              padding: '20px'
            }
          }, [
            loading && React.createElement('div', {
              key: 'loading',
              style: {
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                background: 'rgba(255,255,255,0.95)',
                padding: '40px',
                borderRadius: '12px',
                textAlign: 'center',
                color: '#333',
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                zIndex: 1000,
                minWidth: '300px'
              }
            }, [
              React.createElement('div', {
                key: 'icon',
                style: { fontSize: '48px', marginBottom: '20px' }
              }, '🔄'),
              React.createElement('h3', { 
                key: 'title',
                style: { margin: '0 0 10px 0', color: '#333' }
              }, '正在加载思维导图...'),
              React.createElement('p', { 
                key: 'message',
                style: { margin: 0, opacity: 0.7, fontSize: '14px' }
              }, `文件: ${fileInfo.name}`),
              React.createElement('p', { 
                key: 'format',
                style: { margin: '5px 0 0 0', opacity: 0.7, fontSize: '12px' }
              }, `格式: .${fileInfo.extension}, 大小: ${PreviewerHelper.formatFileSize(fileInfo.size)}`)
            ]),
            
            error && mindMapData && React.createElement('div', {
              key: 'error-info',
              style: {
                position: 'absolute',
                top: '20px',
                left: '50%',
                transform: 'translateX(-50%)',
                background: 'rgba(255, 193, 7, 0.9)',
                padding: '12px 20px',
                borderRadius: '8px',
                textAlign: 'center',
                color: '#856404',
                zIndex: 1000,
                maxWidth: '500px',
                border: '1px solid #ffc107'
              }
            }, [
              React.createElement('div', {
                key: 'icon',
                style: { fontSize: '20px', marginBottom: '8px' }
              }, '⚠️'),
              React.createElement('div', { 
                key: 'message',
                style: { fontSize: '14px', lineHeight: '1.4' }
              }, [
                '文件解析遇到问题，显示简化结构。',
                React.createElement('br', { key: 'br' }),
                '错误: ', error
              ])
            ]),
            
            (!loading || mindMapData) && React.createElement('div', {
              key: 'mindmap-content',
              style: {
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '100%',
                padding: '40px',
                transition: 'all 0.3s ease'
              }
            }, mindMapData ? renderMindMapNode(mindMapData) : null),
            
            renderWatermark()
          ])
        ]);
      };

      ReactDOM.render(React.createElement(MindMapPreviewComponent), previewContainer);

    } catch (error) {
      console.error('加载思维导图预览组件失败:', error);
      MessageBoxUtil.error('思维导图预览组件加载失败: ' + (error instanceof Error ? error.message : '未知错误'));
    }
  }

  private static replaceUrlPlaceholders(template: string, url: string, fileName?: string): string {
    let result = template
      .replace('{originUrl}', url)
      .replace('{b64Url}', encodeURIComponent(this.b64EncodeUnicode(url)))
      .replace('{url}', encodeURIComponent(url));
    if (fileName) {
      result = result.replace(/\{fileName\}/g, encodeURIComponent(fileName));
    }
    return result;
  }

  private static findSuitablePreviewEngine(fileName: string): PreviewEngine | null {
    try {
      const moon = Moon.getSingleton();
      if (!moon || !moon.preference || !moon.preference.previewConfig) {
        return null;
      }
  
      const previewConfig = moon.preference.previewConfig;
  
      // 1. 优先使用自定义引擎
      if (previewConfig.previewEngines) {
        const customEngine = previewConfig.previewEngines.find(engine =>
          engine && engine.canPreview && engine.canPreview(fileName)
        );
        if (customEngine) return customEngine;
      }
  
      // 2. 尝试默认引擎
      const defaultEngines = PreviewEngine.defaultPreviewEngines();
      const matched = defaultEngines.find(engine =>
        engine && engine.canPreview && engine.canPreview(fileName)
      );
      if (matched) return matched;
  
      // 3. 兜底引擎：如果文件属于 OFFICE_PREVIEW_TYPES，构造一个可用的 kkFileView 引擎
      const extension = fileName.split('.').pop()?.toLowerCase();
      if (extension && PreviewerHelper.isOfficeType(extension)) {
        console.log('使用默认 kkFileView 引擎预览:', fileName);
  
        // 尝试从已有引擎配置中提取基础地址（协议+主机+端口）
        let baseUrl = '';
        if (previewConfig.previewEngines && previewConfig.previewEngines.length > 0) {
          const firstEngine = previewConfig.previewEngines[0];
          const urlMatch = firstEngine.url.match(/^(https?:\/\/[^\/]+)/);
          if (urlMatch) {
            baseUrl = urlMatch[1];
          }
        }
  
        // 如果没有找到，则使用当前页面的协议和主机名，端口固定为 8012（可根据实际情况调整）
        if (!baseUrl) {
          // 注意：如果 kkFileView 与应用服务器不同端口，请修改此处端口号
          baseUrl = `${window.location.protocol}//${window.location.hostname}:8012`;
          console.warn('未从引擎配置中提取到基础地址，使用默认地址:', baseUrl);
        }
  
        const fallbackEngine = new PreviewEngine();

        fallbackEngine.url = baseUrl + '/onlinePreview?url={b64Url}&fullfilename={fileName}';
        fallbackEngine.previewInSite = true;
        fallbackEngine.canPreview = () => true;
        return fallbackEngine;
      }
  
      return null;
    } catch (error) {
      console.error('查找预览引擎失败:', error);
      return null;
    }
  }

  static refreshWatermarkConfig(): void {
    this.watermarkConfig = null;
  }

  static formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  private static async parseDXFFile(dxfText: string): Promise<any> {
    try {
      console.log('开始解析DXF文件，字符数:', dxfText.length);
      
      if (!dxfText || dxfText.trim().length === 0) {
        throw new Error('DXF文件内容为空');
      }
      
      const parsedData = this.parseDXFCustom(dxfText);
      
      console.log('✅ DXF解析完成，实体数量:', parsedData.entities?.length || 0);
      return parsedData;
      
    } catch (error) {
      console.error('❌ DXF文件解析失败:', error);
      throw new Error(`DXF解析失败: ${error instanceof Error ? error.message : '未知错误'}`);
    }
  }

  private static parseDXFCustom(dxfText: string): any {
    console.log('🔄 开始解析DXF文件...');
    
    const entities: any[] = [];
    const lines = dxfText.split('\n');
    let currentEntity: any = null;
    let inEntitiesSection = false;
    
    let entityCounts: {[key: string]: number} = {};
    
    for (let i = 0; i < lines.length; i++) {
      const codeLine = lines[i];
      if (!codeLine) continue;
      
      const code = codeLine.trim();
      
      if (code === '0') {
        const valueLine = lines[i + 1];
        if (!valueLine) continue;
        
        const value = valueLine.trim();
        
        if (currentEntity && this.isValidEntity(currentEntity)) {
          entities.push(currentEntity);
          entityCounts[currentEntity.type] = (entityCounts[currentEntity.type] || 0) + 1;
        }
        
        if (value === 'SECTION') {
          const sectionTypeLine = lines[i + 3];
          if (sectionTypeLine?.trim() === 'ENTITIES') {
            inEntitiesSection = true;
            console.log('📖 进入ENTITIES章节');
          } else {
            inEntitiesSection = false;
          }
          i += 2;
          currentEntity = null;
        } 
        else if (value === 'ENDSEC') {
          console.log('📖 离开章节');
          inEntitiesSection = false;
          currentEntity = null;
          i++;
        }
        else if (value === 'EOF') {
          console.log('📄 文件结束');
          break;
        }
        else if (inEntitiesSection) {
          const entityTypes = [
            'LINE', 'LWPOLYLINE', 'POLYLINE', 'CIRCLE', 'ARC', 
            'SPLINE', 'ELLIPSE', 'INSERT', 'POINT', 'TEXT', 'MTEXT', 'DIMENSION'
          ];
          
          if (entityTypes.includes(value)) {
            currentEntity = { type: value };
            console.log(`🆕 开始新实体: ${value}`);
          } else {
            currentEntity = null;
          }
          i++;
        } else {
          currentEntity = null;
          i++;
        }
      } 
      else if (currentEntity && inEntitiesSection) {
        const valueLine = lines[i + 1];
        if (!valueLine) continue;
        
        const value = valueLine.trim();
        
        try {
          switch (code) {
            case '8':
              currentEntity.layer = value;
              console.log(`  图层: ${value}`);
              break;
              
            case '10':
              const xVal = parseFloat(value);
              if (!isNaN(xVal)) {
                this.handleCoordinate(currentEntity, 'x', xVal);
              }
              break;
              
            case '20':
              const yVal = parseFloat(value);
              if (!isNaN(yVal)) {
                this.handleCoordinate(currentEntity, 'y', yVal);
              }
              break;
              
            case '30':
              const zVal = parseFloat(value);
              if (!isNaN(zVal)) {
                this.handleCoordinate(currentEntity, 'z', zVal);
              }
              break;
              
            case '11':
              const x2Val = parseFloat(value);
              if (!isNaN(x2Val)) {
                this.handleSecondCoordinate(currentEntity, 'x', x2Val);
              }
              break;
              
            case '21':
              const y2Val = parseFloat(value);
              if (!isNaN(y2Val)) {
                this.handleSecondCoordinate(currentEntity, 'y', y2Val);
              }
              break;
              
            case '40':
              if (currentEntity.type === 'CIRCLE' || currentEntity.type === 'ARC') {
                const radiusVal = parseFloat(value);
                if (!isNaN(radiusVal)) {
                  currentEntity.radius = radiusVal;
                  console.log(`  半径: ${radiusVal}`);
                }
              }
              break;
              
            case '50':
              if (currentEntity.type === 'ARC') {
                const startAngleVal = parseFloat(value);
                if (!isNaN(startAngleVal)) {
                  currentEntity.startAngle = startAngleVal;
                }
              }
              break;
              
            case '51':
              if (currentEntity.type === 'ARC') {
                const endAngleVal = parseFloat(value);
                if (!isNaN(endAngleVal)) {
                  currentEntity.endAngle = endAngleVal;
                }
              }
              break;
              
            case '70':
              if (currentEntity.type === 'LWPOLYLINE') {
                const flags = parseInt(value);
                currentEntity.closed = (flags & 1) === 1;
                console.log(`  多段线标志: ${flags}, 闭合: ${currentEntity.closed}`);
              }
              break;
          }
        } catch (e) {
          console.warn(`解析坐标时出错: code=${code}, value=${value}`, e);
        }
        
        i++;
      }
    }
    
    if (currentEntity && this.isValidEntity(currentEntity)) {
      entities.push(currentEntity);
      entityCounts[currentEntity.type] = (entityCounts[currentEntity.type] || 0) + 1;
    }
    
    console.log('✅ DXF解析完成');
    console.log('📊 实体统计:', entityCounts);
    
    return { entities };
  }

  private static handleCoordinate(entity: any, coordinate: 'x' | 'y' | 'z', value: number, vertexIndex?: number): void {
    if (!entity) return;
    
    switch (entity.type) {
      case 'LINE':
        if (!entity.start) entity.start = {};
        entity.start[coordinate] = value;
        break;
        
      case 'CIRCLE':
      case 'ARC':
        if (!entity.center) entity.center = {};
        entity.center[coordinate] = value;
        break;
        
      case 'LWPOLYLINE':
      case 'POLYLINE':
        if (!entity.vertices) entity.vertices = [];
        if (vertexIndex !== undefined) {
          if (!entity.vertices[vertexIndex]) {
            entity.vertices[vertexIndex] = {};
          }
          entity.vertices[vertexIndex][coordinate] = value;
        }
        break;
        
      case 'INSERT':
      case 'POINT':
        if (!entity.position) entity.position = {};
        entity.position[coordinate] = value;
        break;
    }
  }

  private static handleSecondCoordinate(entity: any, coordinate: 'x' | 'y' | 'z', value: number, vertexIndex?: number): void {
    if (!entity) return;
    
    switch (entity.type) {
      case 'LINE':
        if (!entity.end) entity.end = {};
        entity.end[coordinate] = value;
        break;
        
      case 'LWPOLYLINE':
      case 'POLYLINE':
        if (entity.vertices && vertexIndex !== undefined) {
          if (!entity.vertices[vertexIndex]) {
            entity.vertices[vertexIndex] = {};
          }
          entity.vertices[vertexIndex][coordinate] = value;
        }
        break;
    }
  }

  private static isValidEntity(entity: any): boolean {
    if (!entity || !entity.type) return false;
    
    switch (entity.type) {
      case 'LINE':
        return entity.start && entity.end && 
               entity.start.x !== undefined && entity.start.y !== undefined &&
               entity.end.x !== undefined && entity.end.y !== undefined;
      case 'CIRCLE':
        return entity.center && entity.radius !== undefined &&
               entity.center.x !== undefined && entity.center.y !== undefined;
      case 'LWPOLYLINE':
        return entity.vertices && entity.vertices.length > 0;
      case 'ARC':
        return entity.center && entity.radius !== undefined && 
               entity.startAngle !== undefined && entity.endAngle !== undefined;
      default:
        return true;
    }
  }

  private static getEntityColor(entity: any): number {
    if (!entity) return 0x4CAF50;
    
    console.log(`🎨 获取实体颜色 - 类型: ${entity.type}, 图层: ${entity.layer}`);
    
    const entityType = (entity.type || '').toUpperCase();
    
    switch (entityType) {
      case 'LINE':
        return 0x4CAF50;
        
      case 'LWPOLYLINE':
      case 'POLYLINE':
        return 0x2196F3;
        
      case 'CIRCLE':
        return 0x9C27B0;
        
      case 'ARC':
        return 0xFF9800;
        
      case 'SPLINE':
        return 0xE91E63;
        
      case 'ELLIPSE':
        return 0x795548;
        
      case 'INSERT':
        return 0xF44336;
        
      case 'TEXT':
      case 'MTEXT':
        return 0x00BCD4;
        
      case 'DIMENSION':
        return 0xCDDC39;
        
      case 'POINT':
        return 0xFFEB3B;
        
      default:
        if (entity.layer) {
          const layerHash = this.stringToHash(entity.layer);
          const layerColors = [
            0x4CAF50, 0x2196F3, 0x9C27B0, 0xFF9800, 0xF44336,
            0x00BCD4, 0x795548, 0xFFEB3B, 0xE91E63, 0x673AB7,
            0x3F51B5, 0x009688, 0x8BC34A, 0xFF5722, 0x607D8B
          ];
          const color = layerColors[layerHash % layerColors.length];
          console.log(`  使用图层颜色: ${entity.layer} -> #${color.toString(16)}`);
          return color;
        }
        
        console.log(`  使用默认颜色: 绿色`);
        return 0x4CAF50;
    }
  }

  private static getEntityLineWidth(entityType: string): number {
    switch (entityType) {
      case 'LINE':
      case 'LWPOLYLINE':
      case 'POLYLINE':
      case 'CIRCLE':
      case 'ARC':
        return 2;
      case 'DIMENSION':
        return 1.5;
      case 'TEXT':
      case 'MTEXT':
      case 'INSERT':
      case 'POINT':
        return 1;
      default:
        return 2;
    }
  }

  private static createGeometryFromEntity(entity: any): LineGeometry | null {
    if (!entity) return null;
    
    console.log(`🔧 创建几何体 - 类型: ${entity.type}, 图层: ${entity.layer}`);
    
    const points: THREE.Vector3[] = [];
    
    try {
      switch (entity.type) {
        case 'LINE':
          if (entity.start && entity.end) {
            const startX = entity.start.x || 0;
            const startY = entity.start.y || 0;
            const startZ = entity.start.z || 0;
            const endX = entity.end.x || 0;
            const endY = entity.end.y || 0;
            const endZ = entity.end.z || 0;
            
            console.log(`  直线: (${startX}, ${startY}, ${startZ}) -> (${endX}, ${endY}, ${endZ})`);
            
            points.push(
              new THREE.Vector3(startX, startY, startZ),
              new THREE.Vector3(endX, endY, endZ)
            );
          } else {
            console.warn('  直线实体缺少起点或终点:', entity);
          }
          break;
          
        case 'LWPOLYLINE':
        case 'POLYLINE':
          if (entity.vertices && Array.isArray(entity.vertices)) {
            console.log(`  多段线 - 顶点数: ${entity.vertices.length}, 闭合: ${entity.closed}`);
            
            entity.vertices.forEach((vertex: any, index: number) => {
              if (vertex && vertex.x !== undefined && vertex.y !== undefined) {
                const x = vertex.x || 0;
                const y = vertex.y || 0;
                const z = vertex.z || 0;
                points.push(new THREE.Vector3(x, y, z));
                console.log(`    顶点${index}: (${x}, ${y}, ${z})`);
              }
            });
            
            if (entity.closed && points.length > 0) {
              points.push(points[0].clone());
              console.log('  闭合多段线，添加起始点');
            }
          } else {
            console.warn('  多段线缺少顶点数据:', entity);
          }
          break;
          
        case 'CIRCLE':
          if (entity.center && entity.radius !== undefined) {
            const centerX = entity.center.x || 0;
            const centerY = entity.center.y || 0;
            const centerZ = entity.center.z || 0;
            const radius = entity.radius;
            
            console.log(`  圆 - 中心: (${centerX}, ${centerY}, ${centerZ}), 半径: ${radius}`);
            
            const segments = 64;
            for (let i = 0; i <= segments; i++) {
              const angle = (i / segments) * Math.PI * 2;
              const x = centerX + Math.cos(angle) * radius;
              const y = centerY + Math.sin(angle) * radius;
              points.push(new THREE.Vector3(x, y, centerZ));
            }
          }
          break;
          
        case 'ARC':
          if (entity.center && entity.radius !== undefined && 
              entity.startAngle !== undefined && entity.endAngle !== undefined) {
            const centerX = entity.center.x || 0;
            const centerY = entity.center.y || 0;
            const centerZ = entity.center.z || 0;
            const radius = entity.radius;
            const startAngle = entity.startAngle;
            const endAngle = entity.endAngle;
            
            console.log(`  圆弧 - 中心: (${centerX}, ${centerY}), 半径: ${radius}, 角度: ${startAngle}°-${endAngle}°`);
            
            const arcSegments = 32;
            const startRad = startAngle * (Math.PI / 180);
            const endRad = endAngle * (Math.PI / 180);
            let angleDiff = endRad - startRad;
            
            if (angleDiff < 0) angleDiff += Math.PI * 2;
            
            for (let i = 0; i <= arcSegments; i++) {
              const angle = startRad + angleDiff * (i / arcSegments);
              const x = centerX + Math.cos(angle) * radius;
              const y = centerY + Math.sin(angle) * radius;
              points.push(new THREE.Vector3(x, y, centerZ));
            }
          }
          break;
          
        default:
          console.log(`⚠️ 未处理的实体类型: ${entity.type}`, entity);
          return null;
      }
      
      if (points.length > 0) {
        const geometry = new LineGeometry();
        const positions: number[] = [];
        
        points.forEach(point => {
          positions.push(point.x, point.y, point.z);
        });
        
        geometry.setPositions(positions);
        console.log(`✅ 几何体创建成功 - 点数: ${points.length}, 类型: ${entity.type}`);
        return geometry;
      }
      
      console.warn(`❌ 无法为实体创建几何体 - 类型: ${entity.type}`);
      return null;
      
    } catch (err) {
      console.error(`💥 处理实体时出错:`, entity.type, err, entity);
      return null;
    }
  }

  private static createDXFGeometryFromEntities(entities: any[]): THREE.Group {
    const group = new THREE.Group();
    group.name = 'DXF_Group';
    
    entities.forEach((entity: any, index: number) => {
      try {
        const geometry = this.createGeometryFromEntity(entity);
        if (geometry) {
          const color = this.getEntityColor(entity);
          const lineWidth = this.getEntityLineWidth(entity.type);
          
          const material = new LineMaterial({
            color: color,
            linewidth: lineWidth,
            worldUnits: false,
            vertexColors: false,
            dashed: false
          });
          
          const mesh = new Line2(geometry, material);
          mesh.computeLineDistances();
          mesh.name = `${entity.type}_${index}`;
          group.add(mesh);
        }
      } catch (e) {
        console.warn(`处理实体 ${index} 失败:`, entity.type, e);
      }
    });
    
    return group;
  }

  private static parseMindMapFile(content: string, extension: string, matter: Matter): any {
    try {
      console.log('开始解析思维导图文件，格式:', extension, '内容长度:', content.length);
      
      let parsedData: any = null;
      
      switch (extension.toLowerCase()) {
        case 'mm':
          try {
            const parser = new DOMParser();
            const xmlDoc = parser.parseFromString(content, 'text/xml');
            parsedData = this.parseFreeMindXML(xmlDoc.documentElement);
          } catch (error) {
            console.warn('FreeMind解析失败，尝试其他方式:', error);
            parsedData = this.tryMultipleParsers(content, extension, matter);
          }
          break;
          
        case 'xmind':
          parsedData = this.tryMultipleParsers(content, extension, matter);
          break;
          
        case 'pos':
          parsedData = this.parsePOSFormat(content, matter);
          break;
          
        case 'eddx':
          parsedData = this.parseEDDXFormat(content, matter);
          break;
          
        case 'json':
        case 'mindmup':
          try {
            parsedData = JSON.parse(content);
            parsedData = this.normalizeMindMapData(parsedData, matter.name.replace(/\.[^/.]+$/, ""));
          } catch (jsonError) {
            console.error('JSON格式错误:', jsonError);
            parsedData = this.tryMultipleParsers(content, extension, matter);
          }
          break;
          
        default:
          parsedData = this.tryMultipleParsers(content, extension, matter);
      }
      
      if (!parsedData) {
        throw new Error('文件解析失败');
      }
      
      if (!parsedData.name || parsedData.name.trim() === '') {
        parsedData.name = matter.name.replace(/\.[^/.]+$/, "");
      }
      if (!parsedData.children || !Array.isArray(parsedData.children)) {
        parsedData.children = [{ name: '中心主题', children: [] }];
      }
      
      console.log('思维导图解析成功:', {
        name: parsedData.name,
        childrenCount: parsedData.children.length
      });
      return parsedData;
    } catch (error) {
      console.error('思维导图解析失败:', error);
      return {
        name: matter.name.replace(/\.[^/.]+$/, ""),
        children: [
          { 
            name: '中心主题', 
            children: [
              { name: '解析失败，显示默认结构', children: [] },
              { name: '文件格式可能不受支持', children: [] },
              { name: '请尝试使用其他格式', children: [] }
            ]
          }
        ]
      };
    }
  }

  private static tryMultipleParsers(content: string, extension: string, matter: Matter): any {
    console.log(`尝试多种解析器处理 .${extension} 格式`);
    
    try {
      const jsonResult = JSON.parse(content);
      console.log(`✅ ${extension}格式JSON解析成功`);
      return this.normalizeMindMapData(jsonResult, matter.name.replace(/\.[^/.]+$/, ""));
    } catch (jsonError) {
      console.log(`❌ ${extension}格式JSON解析失败`);
    }
    
    if (content.trim().startsWith('<?xml') || content.trim().startsWith('<')) {
      try {
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(content, 'text/xml');
        const xmlResult = this.parseGenericXML(xmlDoc);
        console.log(`✅ ${extension}格式XML解析成功`);
        return xmlResult;
      } catch (xmlError) {
        console.log(`❌ ${extension}格式XML解析失败`);
      }
    }
    
    switch (extension.toLowerCase()) {
      case 'pos':
        return this.parsePOSFormat(content, matter);
      case 'eddx':
        return this.parseEDDXFormat(content, matter);
      case 'mm':
        try {
          const parser = new DOMParser();
          const xmlDoc = parser.parseFromString(content, 'text/xml');
          return this.parseFreeMindXML(xmlDoc.documentElement);
        } catch (error) {
          console.warn('FreeMind解析失败:', error);
        }
        break;
    }
    
    const lines = content.split('\n').filter(line => line.trim());
    if (lines.length > 0) {
      return {
        name: matter.name.replace(/\.[^/.]+$/, ""),
        children: lines.slice(0, 5).map((line, index) => ({
          name: line.trim().substring(0, 100) || `行 ${index + 1}`,
          children: []
        }))
      };
    }
    
    return {
      name: matter.name.replace(/\.[^/.]+$/, ""),
      children: [
        { name: '中心主题', children: [] }
      ]
    };
  }

  private static parseGenericXML(xmlDoc: Document): any {
    try {
      const mapElements = xmlDoc.getElementsByTagName('map');
      if (mapElements.length > 0) {
        return this.parseFreeMindXML(mapElements[0]);
      }
      
      const topicElements = xmlDoc.getElementsByTagName('topic');
      if (topicElements.length > 0) {
        return this.parseFreeMindXML(topicElements[0]);
      }
      
      const nodeElements = xmlDoc.getElementsByTagName('node');
      if (nodeElements.length > 0) {
        const rootNode = nodeElements[0];
        return {
          name: rootNode.getAttribute('TEXT') || 'XML思维导图',
          children: Array.from(nodeElements).slice(1).map(node => ({
            name: node.getAttribute('TEXT') || '子节点',
            children: []
          }))
        };
      }
      
    } catch (error) {
      console.warn('通用XML解析失败:', error);
    }
    
    return {
      name: 'XML思维导图',
      children: [{ name: '中心主题', children: [] }]
    };
  }

  private static normalizeMindMapData(data: any, defaultName: string): any {
    const result: any = {
      name: defaultName,
      children: []
    };
    
    try {
      if (data.name || data.text || data.title) {
        result.name = data.name || data.text || data.title || defaultName;
      }
      
      if (data.children && Array.isArray(data.children)) {
        result.children = data.children.map((child: any) => 
          this.normalizeMindMapData(child, '节点')
        );
      } else if (data.nodes && Array.isArray(data.nodes)) {
        result.children = data.nodes.map((node: any) => 
          this.normalizeMindMapData(node, '节点')
        );
      } else if (data.shapes && Array.isArray(data.shapes)) {
        result.children = data.shapes.map((shape: any) => 
          this.normalizeMindMapData(shape, '形状')
        );
      } else if (data.branches && Array.isArray(data.branches)) {
        result.children = data.branches.map((branch: any) => 
          this.normalizeMindMapData(branch, '分支')
        );
      }
      
      if (result.children.length === 0) {
        for (const key in data) {
          if (data[key] && Array.isArray(data[key]) && key !== 'name' && key !== 'text' && key !== 'title') {
            result.children = data[key].slice(0, 5).map((item: any, index: number) => ({
              name: `项目 ${index + 1}`,
              children: []
            }));
            break;
          }
        }
      }
      
    } catch (error) {
      console.warn('规范化思维导图数据失败:', error);
    }
    
    if (result.children.length === 0) {
      result.children = [
        { name: '子节点1', children: [] },
        { name: '子节点2', children: [] }
      ];
    }
    
    return result;
  }

  private static parsePOSFormat(content: string, matter: Matter): any {
    try {
   //   console.log('🔍 开始解析POS格式文件，内容长度:', content.length);
      
      if (content.trim().startsWith('<?xml') || content.trim().startsWith('<')) {
        try {
          const parser = new DOMParser();
          const xmlDoc = parser.parseFromString(content, 'text/xml');
          
          const parserError = xmlDoc.querySelector('parsererror');
          if (parserError) {
          //  console.warn('XML解析错误:', parserError.textContent);
            throw new Error('XML解析错误');
          }
          
       //   console.log('✅ POS格式XML解析成功');
          return this.parseGenericXML(xmlDoc);
          
        } catch (xmlError) {
          //console.warn('POS格式XML解析失败，尝试其他方式:', xmlError);
        }
      }
      
      try {
        const parsed = JSON.parse(content);
        //console.log('✅ POS格式JSON解析成功');
        return this.normalizeMindMapData(parsed, matter.name.replace(/\.[^/.]+$/, ""));
      } catch (jsonError) {
        console.log('⚠️ POS格式JSON解析失败:', jsonError);
      }
      
      const lines = content.split('\n').filter(line => line.trim());
      if (lines.length > 0) {
        //console.log('📝 将POS格式解析为文本大纲，行数:', lines.length);
        return {
          name: matter.name.replace(/\.[^/.]+$/, ""),
          children: lines.slice(0, 10).map((line, index) => ({
            name: line.trim().substring(0, 50) || `主题 ${index + 1}`,
            children: []
          }))
        };
      }
      
      return {
        name: matter.name.replace(/\.[^/.]+$/, ""),
        children: [
          { 
            name: '中心主题', 
            children: [
              { name: '子主题1', children: [] },
              { name: '子主题2', children: [] },
              { name: '子主题3', children: [] }
            ]
          }
        ]
      };
    } catch (error) {
      console.error('POS格式解析失败:', error);
      return {
        name: matter.name.replace(/\.[^/.]+$/, ""),
        children: [
          { name: '中心主题', children: [] }
        ]
      };
    }
  }

  private static parseEDDXFormat(content: string, matter: Matter): any {
    try {
     // console.log('🔍 开始解析EDDX格式文件，内容长度:', content.length);
      
      if (content.trim().startsWith('<?xml') || content.trim().startsWith('<')) {
        try {
          const parser = new DOMParser();
          const xmlDoc = parser.parseFromString(content, 'text/xml');
          
          const parserError = xmlDoc.querySelector('parsererror');
          if (parserError) {
          //  console.warn('XML解析错误:', parserError.textContent);
            throw new Error('XML解析错误');
          }
          
        // console.log('✅ EDDX格式XML解析成功');
          return this.parseGenericXML(xmlDoc);
          
        } catch (xmlError) {
        //  console.warn('EDDX格式XML解析失败，尝试其他方式:', xmlError);
        }
      }
      
      try {
        const parsed = JSON.parse(content);
       // console.log('✅ EDDX格式JSON解析成功');
        return this.normalizeMindMapData(parsed, matter.name.replace(/\.[^/.]+$/, ""));
      } catch (jsonError) {
        //console.warn('EDDX格式JSON解析失败:', jsonError);
      }
      
      return {
        name: matter.name.replace(/\.[^/.]+$/, ""),
        children: [
          { 
            name: 'Edraw思维导图', 
            children: [
              { name: '中心主题', children: [] },
              { name: '分支主题1', children: [] },
              { name: '分支主题2', children: [] }
            ]
          }
        ]
      };
    } catch (error) {
      //console.error('EDDX格式解析异常:', error);
      return {
        name: matter.name.replace(/\.[^/.]+$/, ""),
        children: [
          { name: '中心主题', children: [] }
        ]
      };
    }
  }

  private static parseFreeMindXML(node: Element): any {
    const result: any = { name: '', children: [] };
    
    const textNode = node.getAttribute('TEXT');
    if (textNode) {
      result.name = textNode;
    }
    
    const childNodes = node.children;
    for (let i = 0; i < childNodes.length; i++) {
      const child = childNodes[i];
      if (child.tagName === 'node') {
        const childData = this.parseFreeMindXML(child);
        result.children.push(childData);
      }
    }
    
    return result;
  }

  private static async getSpaceMember(spaceUuid: string, userUuid: string): Promise<any> {
    try {
      const response = await fetch(`/api/space/member/page?spaceUuid=${spaceUuid}&page=0&pageSize=1000&orderCreateTime=desc`);
      
      if (!response.ok) {
       // console.warn(`获取空间成员失败: ${response.status} ${response.statusText}`);
        return null;
      }
      
      const result = await response.json();
      
      if (result.success !== true) {
       // console.warn('获取空间成员API返回失败:', result);
        return null;
      }
      
      const pager = result.data;
      if (!pager || !pager.data || !Array.isArray(pager.data)) {
        //console.warn('空间成员数据结构异常:', pager);
        return null;
      }
      
      const members = pager.data;
      const userMember = members.find((member: any) => 
        member && member.userUuid && member.userUuid === userUuid
      );
      
      if (userMember) {
      /*  console.log('找到空间成员信息:', {
          userUuid: userUuid,
          role: userMember.role,
          spaceUuid: spaceUuid
        });*/
        return userMember;
      }
      
      const total = pager.total || 0;
      const pageSize = pager.pageSize || 20;
      const totalPages = Math.ceil(total / pageSize);
      
      if (totalPages > 1 && pager.page === 0) {
        const maxPagesToSearch = Math.min(totalPages, 5);
        
        for (let page = 1; page < maxPagesToSearch; page++) {
          try {
            const pageResponse = await fetch(
              `/api/space/member/page?spaceUuid=${spaceUuid}&page=${page}&pageSize=${pageSize}&orderCreateTime=desc`
            );
            
            if (pageResponse.ok) {
              const pageResult = await pageResponse.json();
              if (pageResult.success && pageResult.data && Array.isArray(pageResult.data.data)) {
                const pageMembers = pageResult.data.data;
                const foundMember = pageMembers.find((member: any) => 
                  member && member.userUuid && member.userUuid === userUuid
                );
                
                if (foundMember) {
                  console.log('在后续页中找到空间成员信息:', {
                    userUuid: userUuid,
                    role: foundMember.role,
                    spaceUuid: spaceUuid,
                    page: page
                  });
                  return foundMember;
                }
              }
            }
          } catch (pageError) {
            console.warn(`查询第${page}页空间成员失败:`, pageError);
          }
        }
      }
      
      console.log(`用户在空间 ${spaceUuid} 中未找到成员信息`);
      return null;
      
    } catch (error) {
      console.error('获取空间成员信息失败:', error);
      return null;
    }
  }

  private static async checkDownloadPermission(matter: Matter): Promise<boolean> {
    try {
      const moon = Moon.getSingleton();
      const user = moon.user;
      
      if (user.role === UserRole.ADMINISTRATOR) {
        return true;
      }
      
      if (matter.userUuid && matter.userUuid === user.uuid) {
        return true;
      }
      
      if (matter.spaceUuid && user.uuid) {
        try {
          const spaceMember = await this.getSpaceMember(matter.spaceUuid, user.uuid);
          if (spaceMember) {
            const role = spaceMember.role;
            const hasPermission = [SpaceMemberRole.READ_ONLY, SpaceMemberRole.READ_WRITE, SpaceMemberRole.ADMIN].includes(role);
            //console.log(`空间权限检查结果: ${hasPermission}, 用户角色: ${role}`);
            return hasPermission;
          } else {
          //  console.log(`用户 ${user.uuid} 不是空间 ${matter.spaceUuid} 的成员`);
            return false;
          }
        } catch (spaceError) {
          console.warn('检查空间权限失败:', spaceError);
        }
      }
      
      const isPublic = !matter.privacy;
      console.log(`文件隐私状态检查: ${isPublic}, privacy=${matter.privacy}`);
      return isPublic;
      
    } catch (error) {
      console.error('检查下载权限失败:', error);
      return false;
    }
  }

  private static async checkPrintPermission(matter: Matter): Promise<boolean> {
    return this.checkDownloadPermission(matter);
  }

  static b64EncodeUnicode(str: string): string {
    return btoa(
      encodeURIComponent(str).replace(
        /%([0-9A-F]{2})/g,
        (match, p1) => {
          return String.fromCharCode(parseInt('0x' + p1, 16));
        }
      )
    );
  }

  private static async fetchWatermarkConfigFromServer(): Promise<any> {
    try {
      const response = await fetch('/api/preference/watermark-config');
      if (response.ok) {
        const result = await response.json();
        return result;
      }
      return { enabled: false };
    } catch (error) {
      return { enabled: false };
    }
  }
  
  private static async getWatermarkConfig(): Promise<any> {
    try {
      const response = await this.fetchWatermarkConfigFromServer();
      
      let config;
      if (response && response.data) {
        config = response.data;
      } else if (response && response.watermarkConfig) {
        config = response.watermarkConfig;
      } else {
        config = response;
      }
      
      return config;
    } catch (error) {
      return { enabled: false };
    }
  }

  private static async generateWatermarkParams(matter: Matter): Promise<any> {
    try {
      const watermarkConfig = await this.getWatermarkConfig();
      
      const isWatermarkEnabled = watermarkConfig && 
                                watermarkConfig.enabled !== false && 
                                watermarkConfig.enabled !== 'false' &&
                                watermarkConfig.enabled !== 0 &&
                                watermarkConfig.enabled !== '0' &&
                                watermarkConfig.enabled !== undefined &&
                                watermarkConfig.enabled !== null;
      
      console.log('水印配置状态:', {
        配置存在: !!watermarkConfig,
        是否启用: isWatermarkEnabled,
        配置内容: watermarkConfig
      });
      
      if (!isWatermarkEnabled) {
        console.log('水印功能未启用');
        return null;
      }
  
      const watermarkParams = {
        text: watermarkConfig.text || '预览文件',
        fontSize: watermarkConfig.fontSize || 36,
        color: watermarkConfig.color || 'rgba(128, 128, 128, 0.3)',
        opacity: watermarkConfig.opacity || 0.3,
        rotate: watermarkConfig.rotate || -30
      };
  
      const extension = MimeUtil.getExtensionWithoutDot(matter.name)?.toLowerCase();
  
      if (extension === 'pdf') {
        if (watermarkConfig.pdfText) {
          watermarkParams.text = watermarkConfig.pdfText;
        }
        if (watermarkConfig.pdfColor) {
          watermarkParams.color = watermarkConfig.pdfColor;
        }
      } 
      else if (this.isImageType(extension)) {
        if (watermarkConfig.imageText) {
          watermarkParams.text = watermarkConfig.imageText;
        }
        if (watermarkConfig.imageOpacity !== undefined) {
          watermarkParams.opacity = watermarkConfig.imageOpacity;
        }
      }
      else if (this.isOfficeType(extension)) {
        if (watermarkConfig.documentText) {
          watermarkParams.text = watermarkConfig.documentText;
        }
      }
  
      // ✅ 修复：检查user是否存在，避免undefined错误
      const user = Moon.getSingleton()?.user;
      if (user && watermarkConfig.showUser) {
        watermarkParams.text += ` - ${user.username}`;
      } else if (!user && watermarkConfig.showUser) {
        // 如果没有user信息，可以添加默认标识
        watermarkParams.text += ` - 预览用户`;
      }
  
      return watermarkParams;
      
    } catch (error) {
      console.error('生成水印参数失败:', error);
      // 出错时返回null，不影响预览功能
      return null;
    }
  }

  public  static isImageType(extension: string | undefined): boolean {
    const imageTypes = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'];
    return imageTypes.includes(extension || '');
  }

  public  static isDirectPreviewType(extension: string | undefined): boolean {
    return this.DIRECT_PREVIEW_TYPES.includes(extension || '');
  }

  public  static isCadType(extension: string | undefined): boolean {
    const result = this.CAD_PREVIEW_TYPES.includes(extension || '');
  //  console.log(`🔧 isCadType检查: ${extension} -> ${result}`);
    return result;
  }

  public  static isDXFType(extension: string | undefined): boolean {
    const result = this.DXF_PREVIEW_TYPES.includes(extension || '');
    //console.log(`🔧 isDXFType检查: ${extension} -> ${result}`);
    return result;
  }

  public  static isDWGType(extension: string | undefined): boolean {
    const result = this.ODA_CAD_TYPES.includes(extension || '');
   // console.log(`🔧 isDWGType检查: ${extension} -> ${result}`);
    return result;
  }

  public  static isMindMapType(extension: string | undefined): boolean {
    const result = this.MINDMAP_PREVIEW_TYPES.includes(extension || '');
   // console.log(`🧠 isMindMapType检查: ${extension} -> ${result}`);
    return result;
  }

  public  static isOfficeType(extension: string | undefined): boolean {
    return this.OFFICE_PREVIEW_TYPES.includes(extension || '');
  }
/**
 * 获取图片的正确预览 URL（始终包含文件名，不受外部错误 URL 影响）
 */
private static async getCorrectImagePreviewUrl(matter: Matter): Promise<string> {
  return new Promise((resolve) => {
      const buildUrl = (tokenUuid?: string) => {
          let url = `/api/alien/preview/${matter.uuid}/${encodeURIComponent(matter.name)}`;
          if (tokenUuid) {
              url += `?downloadTokenUuid=${encodeURIComponent(tokenUuid)}`;
          }
          if (matter.spaceUuid) {
              const connector = url.includes('?') ? '&' : '?';
              url += `${connector}spaceUuid=${encodeURIComponent(matter.spaceUuid)}`;
          }
          resolve(url);
      };
      if (matter.privacy) {
          const downloadToken = new DownloadToken();
          downloadToken.httpFetchDownloadToken(matter.uuid!, () => {
              buildUrl(downloadToken.uuid!);
          });
      } else {
          buildUrl();
      }
  });
}
  public  static isTextType(extension: string | undefined): boolean {
    if (!extension) return false;
    const textTypes = [
      'txt', 'md', 'json', 'xml', 'yaml', 'yml', 'js', 'ts', 'jsx', 'tsx',
      'html', 'htm', 'css', 'scss', 'less', 'csv', 'log', 'ini', 'conf',
      'properties', 'toml', 'sh', 'bash', 'bat', 'cmd', 'ps1', 'php', 'py',
      'java', 'c', 'cpp', 'cs', 'go', 'rb', 'rs', 'swift'
    ];
    return textTypes.includes(extension);
  }

  private static isAudioType(extension: string | undefined): boolean {
    if (!extension) return false;
    const audioTypes = ['mp3', 'wav', 'ogg', 'm4a', 'aac', 'flac', 'wma', 'aiff'];
    return audioTypes.includes(extension);
  }

  private static isVideoType(extension: string | undefined): boolean {
    if (!extension) return false;
    const videoTypes = ['mp4', 'webm', 'mov', 'avi', 'mkv', 'flv', 'wmv', 'm4v', '3gp', 'mpeg', 'mpg'];
    return videoTypes.includes(extension);
  }

  static prepareMatterUrl(
    matter: Matter,
    needTokenUrl: boolean,
    successCallback: (matterUrl: string) => void
  ): void {
    if (needTokenUrl) {
      const downloadToken = new DownloadToken();
      downloadToken.httpFetchDownloadToken(matter.uuid!, () => {
        successCallback(matter.getPreviewUrl(downloadToken.uuid!));
      });
    } else {
      successCallback(matter.getPreviewUrl());
    }
  }
}

interface RemovableFileInfo {
  name: string;
  size: number;
  isDir: boolean;
  extension?: string;
  modifiedTime?: string;
  path?: string;
  uuid?: string;
  privacy?: boolean;
  spaceUuid?: string;
  userUuid?: string;
}